<template>
  <div>
    <!-- Overlay de guardado -->
    <div v-if="isSubmitting || isSavingEquipos || isSavingPrecios" class="saving-overlay">
      <div class="saving-dialog">
        <b-spinner variant="light" class="mb-2"></b-spinner>
        <div><strong>{{ $t('ficha_venta_guardando') }}</strong></div>
        <small v-if="isSavingEquipos">{{ $t('ficha_venta_guardando_equipos') }}</small>
        <small v-if="isSavingPrecios">{{ $t('ficha_venta_guardando_precios') }}</small>
      </div>
    </div>

    <base-header class="bg-gradient-success">
      <b-container style="max-width: 95%; margin: auto;">

        <!-- Barra de acciones -->
        <div class="d-flex justify-content-end align-items-center mb-3">
          <b-button v-if="puedeEscribir" size="sm" variant="secondary" class="mr-2" @click="resetForm" :disabled="isSubmitting">
            <i class="fas fa-eraser mr-1"></i> {{ $t('limpiar') }}
          </b-button>
          <b-button size="sm" variant="info" class="mr-2" @click="verFichaPdf"
            :disabled="!canVerFicha || isSubmitting" :title="$t('ficha_venta_pdf_nueva_pestana')">
            <i class="fas fa-file-pdf mr-1"></i> {{ $t('ficha_venta_pdf') }}
            <i class="fas fa-external-link-alt ml-1"></i>
          </b-button>
          <b-button v-if="puedeEscribir" size="sm" variant="success" @click="submitForm" :disabled="isSubmitting || isSavingEquipos">
            <i class="fas fa-save mr-1"></i> {{ $t('guardar') }}
          </b-button>
        </div>

        <!-- FORM EDITABLE -->
        <fieldset :disabled="!puedeEscribir" :class="{ 'ficha-solo-lectura': !puedeEscribir }">
        <div>

          <!-- 1. Resumen Ejecutivo -->
          <b-card class="compact-card mb-3">
            <b-card-body class="p-3">
              <h3 class="section-title mb-2">
                <span class="badge badge-primary mr-2">1</span>
                {{ $t('ficha_venta_resumen_ejecutivo') }}
              </h3>
              <label class="mb-1 d-block">{{ $t('ficha_venta_titulo_espanol') }}</label>
              <b-form-input v-model="formData.titulo" maxlength="300"
                :placeholder="$t('ficha_venta_titulo_placeholder')" />

              <label class="mt-3 mb-1 d-block">{{ $t('ficha_venta_resumen_ejecutivo_espanol') }}</label>
              <b-form-textarea v-model="formData.resumenEjecutivo" rows="3" maxlength="2000"
                :class="{ 'is-invalid': !!errors.resumenEjecutivo }"
                @blur="errors.resumenEjecutivo = (!formData.resumenEjecutivo || !formData.resumenEjecutivo.trim()) ? $t('ficha_venta_resumen_requerido') : null"
                :placeholder="$t('ficha_venta_resumen_placeholder')" />
              <div v-if="errors.resumenEjecutivo" class="invalid-feedback d-block">{{ errors.resumenEjecutivo }}</div>
              <small class="text-muted float-right">{{ resumenEjecutivoLength }}/2000</small>

              <label class="mt-3 mb-1 d-block">{{ $t('ficha_venta_titulo_ingles') }}</label>
              <b-form-input v-model="formData.tituloIngles" maxlength="300"
                :placeholder="$t('ficha_venta_titulo_placeholder_ingles')" />

              <label class="mt-3 mb-1 d-block">{{ $t('ficha_venta_resumen_ejecutivo_ingles') }}</label>
              <b-form-textarea v-model="formData.resumenEjecutivoIngles" rows="3" maxlength="2000"
                :placeholder="$t('ficha_venta_resumen_placeholder_ingles')" />
              <small class="text-muted float-right">{{ resumenEjecutivoInglesLength }}/2000</small>
            </b-card-body>
          </b-card>

          <!-- 2. Información General y Técnica -->
          <b-card class="compact-card mb-3">
            <b-card-body class="p-3">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h3 class="section-title mb-0">
                  <span class="badge badge-primary mr-2">2</span>
                  {{ $t('ficha_venta_info_general') }}
                </h3>
                <div class="d-flex align-items-center">
                  <span class="text-muted small mr-2">Vistas Pre-Seteadas:</span>
                  <el-select v-model="tipoFichaSeleccionado" size="small" style="width: 140px;" class="mr-2">
                    <el-option :value="1" :label="$t('ficha_tipo_equipos')" />
                    <el-option :value="2" :label="$t('ficha_tipo_repuestos')" />
                    <el-option :value="3" :label="$t('ficha_tipo_correas')" />
                    <el-option :value="4" :label="$t('ficha_tipo_neumaticos')" />
                  </el-select>
                  <b-button size="sm" variant="outline-primary" @click="expandirColumnas = !expandirColumnas">
                    <i :class="expandirColumnas ? 'fas fa-compress-alt' : 'fas fa-expand-alt'" class="mr-1"></i>
                    {{ expandirColumnas ? $t('ficha_venta_con_valor') : $t('ficha_venta_ver_todo') }}
                  </b-button>
                  <b-button size="sm" variant="outline-secondary" class="ml-1"
                    :title="$t('resetear_orden')" @click="resetearOrden"
                    :disabled="!infoGeneral.length">
                    <i class="fas fa-undo"></i>
                  </b-button>
                </div>
              </div>

              <!-- Primera tabla - Solo para EQUIPOS -->
              <div v-if="this.tipoFichaSeleccionado === 1" class="table-responsive"
                style="height: 300px; overflow-y: auto;">
                <div v-if="infoLoading" class="text-muted small mb-2">{{ $t('ficha_venta_cargando_info') }}</div>
                <div v-else-if="infoGeneral.length === 0" class="text-muted small mb-2">{{ $t('ficha_venta_sin_info') }}</div>

                <table v-else class="table table-sm table-bordered">
                  <thead class="thead-light">
                    <tr>
                      <th class="th-ordenable" @click="ordenarTabla('orden')" :title="$t('ordenar')"># ↕️</th>
                      <th v-if="mostrarColumnaDescripcionCorta" class="th-ordenable" @click="ordenarTabla('partName')" :title="$t('ordenar')">{{ $t('descripcion_corta') }} {{ iconoOrden('partName') }}</th>
                      <th class="th-ordenable" @click="ordenarTabla('categoria')" :title="$t('ordenar')">{{ $t('categoria') }} {{ iconoOrden('categoria') }}</th>
                      <th v-if="mostrarColumnaNParte" class="th-ordenable col-nparte" @click="ordenarTabla('numeroSerie')" :title="$t('ordenar')">{{ $t('n_parte') }} {{ iconoOrden('numeroSerie') }}</th>
                      <th class="text-right th-ordenable" @click="ordenarTabla('precio')" :title="$t('ordenar')">
                        {{ $t('precio') }} {{ iconoOrden('precio') }}
                      </th>
                      <th v-if="mostrarColumnaModelo">{{ $t('modelo') }}</th>
                      <th v-if="mostrarColumnaFabricante" class="th-ordenable" @click="ordenarTabla('fabricante')" :title="$t('ordenar')">{{ $t('fabricante') }} {{ iconoOrden('fabricante') }}</th>
                      <th v-if="mostrarColumnaAnoFabricacion">{{ $t('ano_fabricacion') }}</th>
                      <th v-if="mostrarColumnaHorasOperacion">{{ $t('horas_operacion') }}</th>
                      <th class="text-right">{{ $t('cantidad') }}</th>
                      <th v-if="mostrarColumnaUnidadMedida">{{ $t('unidad_medida') }}</th>
                      <th>{{ $t('condicion_uso') }}</th>
                      <th v-if="mostrarColumnaTamanoNeumaticos">{{ $t('tamano_condicion_neumaticos') }}</th>
                      <th v-if="mostrarColumnaHistorialMantenimiento">{{ $t('historial_mantenimiento') }}</th>
                      <th v-if="mostrarColumnaReparacionesRevisionesReciente">{{ $t('reparaciones_recientes') }}</th>
                      <th v-if="mostrarMultimedia" class="text-center" style="width:110px;">{{ $t('multimedia') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in infoGeneralPaginado" :key="item.idx">
                      <td style="white-space:nowrap; text-align:center;">
                        <span>{{ item.row.orden || (item.idx + 1) }}</span>
                        <b-button size="sm" variant="link" class="p-0 ml-1" style="vertical-align:middle; font-size:1em; line-height:1;" :title="$t('mover')" @click="abrirModalMover(item.idx)">↕️</b-button>
                      </td>
                      <td v-if="mostrarColumnaDescripcionCorta">{{ safe(item.row, 'partName') }}</td>
                      <td>{{ safe(item.row, 'categoria') }}</td>
                      <td v-if="mostrarColumnaNParte" class="col-nparte" :title="safe(item.row, 'numeroSerie')">{{ safe(item.row, 'numeroSerie') }}</td>
                      <td class="text-right">{{ formatPrecio(item.row.precioOriginal) }}</td>
                      <td v-if="mostrarColumnaModelo">{{ safe(item.row, 'modelo') }}</td>
                      <td v-if="mostrarColumnaFabricante">{{ safe(item.row, 'fabricante') }}</td>
                      <td v-if="mostrarColumnaAnoFabricacion">{{ safe(item.row, 'anoFabricacion') }}</td>
                      <td v-if="mostrarColumnaHorasOperacion">{{ safe(item.row, 'horasOperacion') }}</td>
                      <td class="text-right">
                        {{ item.row.cantidad != null && item.row.cantidad !== '' && item.row.cantidad !== 0
                          ? Number(item.row.cantidad).toLocaleString('es-CL')
                          : '—' }}
                      </td>
                      <td v-if="mostrarColumnaUnidadMedida">{{ item.row.unidadDeMedida || '—' }}</td>
                      <td>{{ resolveCondicion(safe(item.row, 'condicion')) }}</td>
                      <td v-if="mostrarColumnaTamanoNeumaticos">
                        <b-form-textarea v-model="item.row.tamanoCondicionNeumaticos" rows="2" size="sm"
                          :placeholder="$t('ingrese_tamano_neumaticos')" />
                      </td>
                      <td v-if="mostrarColumnaHistorialMantenimiento">{{ safe(item.row, 'HistorialMantenimiento') }}</td>
                      <td v-if="mostrarColumnaReparacionesRevisionesReciente">
                        <b-form-textarea v-model="item.row.reparacionesRevisionesRecientes" rows="2" size="sm"
                          :placeholder="$t('ingrese_reparaciones')" />
                      </td>
                      <td v-if="mostrarMultimedia" class="text-center">
                        <b-button size="sm" :variant="tieneMultimedia(item.row) ? 'success' : 'info'"
                          :title="tituloMultimedia(item.row)" @click="abrirMultimedia(item.row)">
                          <i class="fas fa-images"></i>
                          <span v-if="tieneMultimedia(item.row)" class="ml-1">{{ (item.row.cantidadFotos || 0) + (item.row.cantidadVideos || 0) }}</span>
                        </b-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="infoGeneral.length > pageSizeMateriales" class="d-flex align-items-center justify-content-between mt-2 small">
                  <span class="text-muted">{{ $t('pagina') }} {{ currentPageMateriales }} {{ $t('de') }} {{ totalPagesMateriales }} ({{ infoGeneral.length }} {{ $t('items_label') }})</span>
                  <div>
                    <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="currentPageMateriales === 1" @click="currentPageMateriales--"><i class="fas fa-chevron-left"></i></button>
                    <button type="button" class="btn btn-sm btn-outline-secondary ml-1" :disabled="currentPageMateriales >= totalPagesMateriales" @click="currentPageMateriales++"><i class="fas fa-chevron-right"></i></button>
                  </div>
                </div>
              </div>


              <!-- Segunda tabla - COMPONENTES -->
              <div v-if="this.tipoFichaSeleccionado === 2" class="table-responsive mt-3"
                style="height: 300px; overflow-y: auto;">
                <div v-if="infoLoading" class="text-muted small mb-2">{{ $t('ficha_venta_cargando_info') }}</div>
                <div v-else-if="infoGeneral.length === 0" class="text-muted small mb-2">{{ $t('ficha_venta_sin_info') }}</div>

                <table v-else class="table table-sm table-bordered">
                  <thead class="thead-light">
                    <tr>
                      <th class="th-ordenable" @click="ordenarTabla('orden')" :title="$t('ordenar')"># ↕️</th>
                      <th v-if="mostrarColumnaDescripcionCorta" class="th-ordenable" @click="ordenarTabla('partName')" :title="$t('ordenar')">{{ $t('descripcion_corta') }} {{ iconoOrden('partName') }}</th>
                      <th class="th-ordenable" @click="ordenarTabla('categoria')" :title="$t('ordenar')">{{ $t('categoria') }} {{ iconoOrden('categoria') }}</th>
                      <th v-if="mostrarColumnaNParte" class="th-ordenable col-nparte" @click="ordenarTabla('numeroSerie')" :title="$t('ordenar')">{{ $t('n_parte') }} {{ iconoOrden('numeroSerie') }}</th>
                      <th class="text-right th-ordenable" @click="ordenarTabla('precio')" :title="$t('ordenar')">
                        {{ $t('precio') }} {{ iconoOrden('precio') }}
                      </th>
                      <th v-if="mostrarColumnaFabricante" class="th-ordenable" @click="ordenarTabla('fabricante')" :title="$t('ordenar')">{{ $t('fabricante') }} {{ iconoOrden('fabricante') }}</th>
                      <th v-if="mostrarColumnaModelo">{{ $t('modelo') }}</th>
                      <th class="text-right">{{ $t('cantidad') }}</th>
                      <th v-if="mostrarColumnaUnidadMedida">{{ $t('unidad_medida') }}</th>
                      <th>{{ $t('condicion_uso') }}</th>
                      <th v-if="mostrarMultimedia" class="text-center" style="width:110px;">{{ $t('multimedia') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in infoGeneralPaginado" :key="'segunda-' + item.idx">
                      <td style="white-space:nowrap; text-align:center;">
                        <span>{{ item.row.orden || (item.idx + 1) }}</span>
                        <b-button size="sm" variant="link" class="p-0 ml-1" style="vertical-align:middle; font-size:1em; line-height:1;" :title="$t('mover')" @click="abrirModalMover(item.idx)">↕️</b-button>
                      </td>
                      <td v-if="mostrarColumnaDescripcionCorta">{{ safe(item.row, 'partName') }}</td>
                      <td>{{ safe(item.row, 'categoria') }}</td>
                      <td v-if="mostrarColumnaNParte" class="col-nparte" :title="safe(item.row, 'numeroSerie')">{{ safe(item.row, 'numeroSerie') }}</td>
                      <td class="text-right">{{ formatPrecio(item.row.precioOriginal) }}</td>
                      <td v-if="mostrarColumnaFabricante">{{ safe(item.row, 'fabricante') }}</td>
                      <td v-if="mostrarColumnaModelo">{{ safe(item.row, 'modelo') }}</td>
                      <td class="text-right">
                        {{ item.row.cantidad != null && item.row.cantidad !== '' && item.row.cantidad !== 0
                          ? Number(item.row.cantidad).toLocaleString('es-CL')
                          : '—' }}
                      </td>
                      <td v-if="mostrarColumnaUnidadMedida">{{ item.row.unidadDeMedida || '—' }}</td>
                      <td>{{ resolveCondicion(safe(item.row, 'condicion')) }}</td>
                      <td v-if="mostrarMultimedia" class="text-center">
                        <b-button size="sm" :variant="tieneMultimedia(item.row) ? 'success' : 'info'"
                          :title="tituloMultimedia(item.row)" @click="abrirMultimedia(item.row)">
                          <i class="fas fa-images"></i>
                          <span v-if="tieneMultimedia(item.row)" class="ml-1">{{ (item.row.cantidadFotos || 0) + (item.row.cantidadVideos || 0) }}</span>
                        </b-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="infoGeneral.length > pageSizeMateriales" class="d-flex align-items-center justify-content-between mt-2 small">
                  <span class="text-muted">{{ $t('pagina') }} {{ currentPageMateriales }} {{ $t('de') }} {{ totalPagesMateriales }} ({{ infoGeneral.length }} {{ $t('items_label') }})</span>
                  <div>
                    <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="currentPageMateriales === 1" @click="currentPageMateriales--"><i class="fas fa-chevron-left"></i></button>
                    <button type="button" class="btn btn-sm btn-outline-secondary ml-1" :disabled="currentPageMateriales >= totalPagesMateriales" @click="currentPageMateriales++"><i class="fas fa-chevron-right"></i></button>
                  </div>
                </div>
              </div>

              <!-- Tercera tabla - Solo para CORREAS -->
              <div v-if="this.tipoFichaSeleccionado === 3" class="table-responsive mt-3"
                style="height: 300px; overflow-y: auto;">
                <div v-if="infoLoading" class="text-muted small mb-2">{{ $t('ficha_venta_cargando_info') }}</div>
                <div v-else-if="infoGeneral.length === 0" class="text-muted small mb-2">{{ $t('ficha_venta_sin_info') }}</div>

                <table v-else class="table table-sm table-bordered">
                  <thead class="thead-light">
                    <tr>
                      <th class="th-ordenable" @click="ordenarTabla('orden')" :title="$t('ordenar')"># ↕️</th>
                      <th v-if="mostrarColumnaDescripcionCorta" class="th-ordenable" @click="ordenarTabla('partName')" :title="$t('ordenar')">{{ $t('descripcion_corta') }} {{ iconoOrden('partName') }}</th>
                      <th class="th-ordenable" @click="ordenarTabla('categoria')" :title="$t('ordenar')">{{ $t('categoria') }} {{ iconoOrden('categoria') }}</th>
                      <th v-if="mostrarColumnaNParte" class="th-ordenable col-nparte" @click="ordenarTabla('numeroSerie')" :title="$t('ordenar')">{{ $t('n_parte') }} {{ iconoOrden('numeroSerie') }}</th>
                      <th class="text-right th-ordenable" @click="ordenarTabla('precio')" :title="$t('ordenar')">
                        {{ $t('precio') }} {{ iconoOrden('precio') }}
                      </th>
                      <th v-if="mostrarColumnaFabricante" class="th-ordenable" @click="ordenarTabla('fabricante')" :title="$t('ordenar')">{{ $t('fabricante') }} {{ iconoOrden('fabricante') }}</th>
                      <th v-if="mostrarColumnaModelo">{{ $t('modelo') }}</th>
                      <th v-if="mostrarColumnaDimensiones">{{ $t('dimensiones_mm') }}</th>
                      <th v-if="mostrarColumnaTipo">{{ $t('tipo') }}</th>
                      <th class="text-right">{{ $t('cantidad') }}</th>
                      <th v-if="mostrarColumnaUnidadMedida">{{ $t('unidad_medida') }}</th>
                      <th>{{ $t('condicion_uso') }}</th>
                      <th v-if="mostrarColumnaUsoRecomendado">{{ $t('uso_recomendado') }}</th>
                      <th v-if="mostrarColumnaSistemasCompatibles">{{ $t('sistemas_compatibles') }}</th>
                      <th v-if="mostrarMultimedia" class="text-center" style="width:110px;">{{ $t('multimedia') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in infoGeneralPaginado" :key="'correa-' + item.idx">
                      <td style="white-space:nowrap; text-align:center;">
                        <span>{{ item.row.orden || (item.idx + 1) }}</span>
                        <b-button size="sm" variant="link" class="p-0 ml-1" style="vertical-align:middle; font-size:1em; line-height:1;" :title="$t('mover')" @click="abrirModalMover(item.idx)">↕️</b-button>
                      </td>
                      <td v-if="mostrarColumnaDescripcionCorta">{{ safe(item.row, 'partName') }}</td>
                      <td>{{ safe(item.row, 'categoria') }}</td>
                      <td v-if="mostrarColumnaNParte" class="col-nparte" :title="safe(item.row, 'numeroSerie')">{{ safe(item.row, 'numeroSerie') }}</td>
                      <td class="text-right">{{ formatPrecio(item.row.precioOriginal) }}</td>
                      <td v-if="mostrarColumnaFabricante">{{ safe(item.row, 'fabricante') }}</td>
                      <td v-if="mostrarColumnaModelo">{{ safe(item.row, 'modelo') }}</td>
                      <td v-if="mostrarColumnaDimensiones">
                        <b-form-textarea v-model="item.row.dimensiones" rows="2" size="sm"
                          :placeholder="$t('ingrese_dimensiones_mm')" />
                      </td>
                      <td v-if="mostrarColumnaTipo">
                        <b-form-textarea v-model="item.row.tipo" rows="2" size="sm"
                          :placeholder="$t('ingrese_tipo')" />
                      </td>
                      <td class="text-right">
                        {{ item.row.cantidad != null && item.row.cantidad !== '' && item.row.cantidad !== 0
                          ? Number(item.row.cantidad).toLocaleString('es-CL')
                          : '—' }}
                      </td>
                      <td v-if="mostrarColumnaUnidadMedida">{{ item.row.unidadDeMedida || '—' }}</td>
                      <td>{{ resolveCondicion(safe(item.row, 'condicion')) }}</td>
                      <td v-if="mostrarColumnaUsoRecomendado">
                        <b-form-textarea v-model="item.row.usoRecomendado" rows="2" size="sm"
                          :placeholder="$t('ingrese_uso_recomendado')" />
                      </td>
                      <td v-if="mostrarColumnaSistemasCompatibles">
                        <b-form-textarea v-model="item.row.sistemasCompatibles" rows="2" size="sm"
                          :placeholder="$t('ingrese_sistemas_compatibles')" />
                      </td>
                      <td v-if="mostrarMultimedia" class="text-center">
                        <b-button size="sm" :variant="tieneMultimedia(item.row) ? 'success' : 'info'"
                          :title="tituloMultimedia(item.row)" @click="abrirMultimedia(item.row)">
                          <i class="fas fa-images"></i>
                          <span v-if="tieneMultimedia(item.row)" class="ml-1">{{ (item.row.cantidadFotos || 0) + (item.row.cantidadVideos || 0) }}</span>
                        </b-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="infoGeneral.length > pageSizeMateriales" class="d-flex align-items-center justify-content-between mt-2 small">
                  <span class="text-muted">{{ $t('pagina') }} {{ currentPageMateriales }} {{ $t('de') }} {{ totalPagesMateriales }} ({{ infoGeneral.length }} {{ $t('items_label') }})</span>
                  <div>
                    <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="currentPageMateriales === 1" @click="currentPageMateriales--"><i class="fas fa-chevron-left"></i></button>
                    <button type="button" class="btn btn-sm btn-outline-secondary ml-1" :disabled="currentPageMateriales >= totalPagesMateriales" @click="currentPageMateriales++"><i class="fas fa-chevron-right"></i></button>
                  </div>
                </div>
              </div>

              <!-- Cuarta tabla - Solo para NEUMATICOS -->
              <div v-if="this.tipoFichaSeleccionado === 4" class="table-responsive mt-3"
                style="height: 300px; overflow-y: auto;">
                <div v-if="infoLoading" class="text-muted small mb-2">{{ $t('ficha_venta_cargando_info') }}</div>
                <div v-else-if="infoGeneral.length === 0" class="text-muted small mb-2">{{ $t('ficha_venta_sin_info') }}</div>

                <table v-else class="table table-sm table-bordered">
                  <thead class="thead-light">
                    <tr>
                      <th class="th-ordenable" @click="ordenarTabla('orden')" :title="$t('ordenar')"># ↕️</th>
                      <th v-if="mostrarColumnaDescripcionCorta" class="th-ordenable" @click="ordenarTabla('partName')" :title="$t('ordenar')">{{ $t('descripcion_corta') }} {{ iconoOrden('partName') }}</th>
                      <th class="th-ordenable" @click="ordenarTabla('categoria')" :title="$t('ordenar')">{{ $t('categoria') }} {{ iconoOrden('categoria') }}</th>
                      <th v-if="mostrarColumnaNParte" class="th-ordenable col-nparte" @click="ordenarTabla('numeroSerie')" :title="$t('ordenar')">{{ $t('n_parte') }} {{ iconoOrden('numeroSerie') }}</th>
                      <th class="text-right th-ordenable" @click="ordenarTabla('precio')" :title="$t('ordenar')">
                        {{ $t('precio') }} {{ iconoOrden('precio') }}
                      </th>
                      <th v-if="mostrarColumnaFabricante" class="th-ordenable" @click="ordenarTabla('fabricante')" :title="$t('ordenar')">{{ $t('fabricante') }} {{ iconoOrden('fabricante') }}</th>
                      <th v-if="mostrarColumnaModelo">{{ $t('modelo') }}</th>
                      <th v-if="mostrarColumnaDimensiones" style="min-width: 120px;">{{ $t('tamano') }}</th>
                      <th v-if="mostrarColumnaTipo" style="min-width: 150px;">{{ $t('tipo') }}</th>
                      <th class="text-right">{{ $t('cantidad') }}</th>
                      <th v-if="mostrarColumnaUnidadMedida">{{ $t('unidad_medida') }}</th>
                      <th>{{ $t('condicion_uso') }}</th>
                      <th v-if="mostrarColumnaSistemasCompatibles">{{ $t('equipo_compatible') }}</th>
                      <th v-if="mostrarColumnaProfundidadBanda">{{ $t('profundidad_banda') }}</th>
                      <th v-if="mostrarMultimedia" class="text-center" style="width:110px;">{{ $t('multimedia') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in infoGeneralPaginado" :key="'neumatico-' + item.idx">
                      <td style="white-space:nowrap; text-align:center;">
                        <span>{{ item.row.orden || (item.idx + 1) }}</span>
                        <b-button size="sm" variant="link" class="p-0 ml-1" style="vertical-align:middle; font-size:1em; line-height:1;" :title="$t('mover')" @click="abrirModalMover(item.idx)">↕️</b-button>
                      </td>
                      <td v-if="mostrarColumnaDescripcionCorta">{{ safe(item.row, 'partName') }}</td>
                      <td>{{ safe(item.row, 'categoria') }}</td>
                      <td v-if="mostrarColumnaNParte" class="col-nparte" :title="safe(item.row, 'numeroSerie')">{{ safe(item.row, 'numeroSerie') }}</td>
                      <td class="text-right">{{ formatPrecio(item.row.precioOriginal) }}</td>
                      <td v-if="mostrarColumnaFabricante">{{ safe(item.row, 'fabricante') }}</td>
                      <td v-if="mostrarColumnaModelo">{{ safe(item.row, 'modelo') }}</td>
                      <td v-if="mostrarColumnaDimensiones">
                        <b-form-textarea v-model="item.row.dimensiones" rows="2" size="sm"
                          :placeholder="$t('ingrese_tamano')" />
                      </td>
                      <td v-if="mostrarColumnaTipo">
                        <b-form-textarea v-model="item.row.tipo" rows="2" size="sm"
                          :placeholder="$t('ingrese_tipo')" />
                      </td>
                      <td class="text-right">
                        {{ item.row.cantidad != null && item.row.cantidad !== '' && item.row.cantidad !== 0
                          ? Number(item.row.cantidad).toLocaleString('es-CL')
                          : '—' }}
                      </td>
                      <td v-if="mostrarColumnaUnidadMedida">{{ item.row.unidadDeMedida || '—' }}</td>
                      <td>{{ resolveCondicion(safe(item.row, 'condicion')) }}</td>
                      <td v-if="mostrarColumnaSistemasCompatibles">
                        <b-form-textarea v-model="item.row.sistemasCompatibles" rows="2" size="sm"
                          :placeholder="$t('ingrese_equipo_compatible')" />
                      </td>
                      <td v-if="mostrarColumnaProfundidadBanda">
                        <b-form-textarea v-model="item.row.profundidadBanda" size="sm"
                          :placeholder="$t('ingrese_profundidad_banda')" />
                      </td>
                      <td v-if="mostrarMultimedia" class="text-center">
                        <b-button size="sm" :variant="tieneMultimedia(item.row) ? 'success' : 'info'"
                          :title="tituloMultimedia(item.row)" @click="abrirMultimedia(item.row)">
                          <i class="fas fa-images"></i>
                          <span v-if="tieneMultimedia(item.row)" class="ml-1">{{ (item.row.cantidadFotos || 0) + (item.row.cantidadVideos || 0) }}</span>
                        </b-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="infoGeneral.length > pageSizeMateriales" class="d-flex align-items-center justify-content-between mt-2 small">
                  <span class="text-muted">{{ $t('pagina') }} {{ currentPageMateriales }} {{ $t('de') }} {{ totalPagesMateriales }} ({{ infoGeneral.length }} {{ $t('items_label') }})</span>
                  <div>
                    <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="currentPageMateriales === 1" @click="currentPageMateriales--"><i class="fas fa-chevron-left"></i></button>
                    <button type="button" class="btn btn-sm btn-outline-secondary ml-1" :disabled="currentPageMateriales >= totalPagesMateriales" @click="currentPageMateriales++"><i class="fas fa-chevron-right"></i></button>
                  </div>
                </div>
              </div>

            </b-card-body>
          </b-card>

          <!-- Modal Mover item (estilo Trello) -->
          <b-modal id="modal-mover-item" v-model="showModalMover" :title="$t('mover_item')" size="sm"
            :ok-title="$t('mover')" :cancel-title="$t('cancelar')" :ok-disabled="guardandoOrden"
            @ok="confirmarMoverItem">
            <p class="mb-2">{{ $t('mover_item_posicion') }}</p>
            <b-form-input type="number" min="1" :max="infoGeneral.length" v-model.number="posicionMover" />
            <small class="text-muted">{{ $t('mover_item_total', { total: infoGeneral.length }) }}</small>
          </b-modal>

          <!-- 3. Registro Fotográfico -->
          <b-card class="compact-card mb-3">
            <b-card-body class="p-3">
              <h3 class="section-title mb-3">
                <span class="badge badge-primary mr-2">3</span>
                {{ $t('ficha_venta_registro_fotografico') }}
              </h3>

              <div v-if="fotosLoading || videosLoading" class="text-muted small mb-2">
                <b-spinner small class="mr-2"></b-spinner>{{ $t('cargando_fotos_videos') }}
              </div>

              <!-- Photos -->
              <div class="mb-3">
                <small class="form-label d-block mb-2 font-weight-bold">{{ $t('fotos') }}</small>
                <div v-if="fotos.length === 0">
                  <b-row v-if="imagenCategoriaDefecto">
                    <b-col cols="6" md="3" class="mb-3">
                      <div class="border rounded p-1">
                        <img :src="imagenCategoriaDefecto" alt="Imagen por defecto de la categoría" class="img-fluid rounded"
                          style="max-height: 300px; width: 100%; object-fit: contain;" />
                        <div class="mt-1">
                          <small class="text-muted">{{ $t('imagen_defecto_categoria') }}</small>
                        </div>
                      </div>
                    </b-col>
                  </b-row>
                  <div v-else class="text-muted small">{{ $t('sin_fotos') }}</div>
                </div>
                <b-row v-else>
                  <b-col v-for="f in fotos" :key="f.Id || f.id" cols="6" md="3" class="mb-3">
                    <div class="border rounded p-1">
                      <img :src="f.Url || f.url" alt="Foto cluster" class="img-fluid rounded" />
                      <div class="mt-1">
                        <small class="text-muted">{{ (f.FechaSubida || f.fechaSubida) ? new Date(f.FechaSubida ||
                          f.fechaSubida).toLocaleString() : '' }}</small>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </div>

              <!-- Videos -->
              <div>
                <small class="form-label d-block mb-2 font-weight-bold">{{ $t('videos') }}</small>
                <div v-if="videos.length === 0" class="text-muted small">{{ $t('sin_videos') }}</div>
                <b-row v-else>
                  <b-col v-for="v in videos" :key="v.Id || v.id" cols="12" md="6" class="mb-3">
                    <div class="border rounded p-2">
                      <div class="embed-responsive embed-responsive-16by9" v-if="isYouTube(v.Url || v.url)">
                        <iframe class="embed-responsive-item" :src="toYouTubeEmbed(v.Url || v.url)" frameborder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowfullscreen>
                        </iframe>
                      </div>
                      <video v-else controls style="width: 100%;">
                        <source :src="v.Url || v.url" type="video/mp4" />
                      </video>
                      <div class="mt-1">
                        <small class="text-muted">{{ (v.FechaCreacion || v.fechaCreacion) ? new Date(v.FechaCreacion ||
                          v.fechaCreacion).toLocaleString() : '' }}</small>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </div>
            </b-card-body>
          </b-card>

          <!-- 4. Términos y Condiciones -->
          <b-card class="compact-card mb-3">
            <b-card-body class="p-3">
              <h3 class="section-title mb-3">
                <span class="badge badge-primary mr-2">4</span>
                {{ $t('ficha_venta_terminos') }}
              </h3>

              <b-row>
                <b-col md="6" class="mb-3">
                  <small class="form-label d-block mb-1 font-weight-bold">{{ $t('hay_precio_definido') }}</small>
                  <b-form-radio-group v-model="formData.tienePrecio" :options="opcionesPrecio" name="precio"
                    size="sm" :class="{ 'is-invalid': !!errors.tienePrecio }" />
                  <div v-if="errors.tienePrecio" class="invalid-feedback d-block">{{ errors.tienePrecio }}</div>
                </b-col>
              </b-row>

              <!-- Tabla de Precios del endpoint, visible solo si hay precio -->
              <b-collapse :visible="formData.tienePrecio === true" class="mt-2">
                <div class="table-responsive" style="height: 250px; overflow-y: auto;">
                  <div v-if="infoLoading" class="text-muted small mb-2">{{ $t('cargando_precios') }}</div>
                  <div v-else-if="infoTerminosYCondiciones.length === 0" class="text-muted small mb-2">{{ $t('sin_precios') }}</div>
                  <table v-else class="table table-sm table-bordered">
                    <thead class="thead-light">
                      <tr>
                        <th>#</th>
                        <th v-if="mostrarColumnaDescripcionCorta">{{ $t('descripcion_corta') }}</th>
                        <th>{{ $t('categoria') }}</th>
                        <th>{{ $t('unidad_medida') }}</th>
                        <th class="text-right">{{ $t('cantidad') }}</th>
                        <th>{{ $t('moneda') }}</th>
                        <th class="text-right" style="width: 110px;">{{ $t('precio_neto') }}</th>
                        <th class="text-right">{{ $t('monto_neto') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, idx) in infoTerminosYCondiciones" :key="'prev-precio-' + idx">
                        <td>{{ idx + 1 }}</td>
                        <td>{{ safe(row, 'PartName') || safe(row, 'Detalle') }}</td>
                        <td>{{ safe(row, 'Categoria') }}</td>
                        <td>{{ safe(row, 'UnidadDeMedida') }}</td>
                        <td class="text-right">
                          {{ safe(row, 'Cantidad') && safe(row, 'Cantidad') !== '—'
                            ? Number(safe(row, 'Cantidad')).toLocaleString('es-CL')
                            : safe(row, 'Cantidad') }}
                        </td>
                        <td>
                          <b-form-select v-model="row.Moneda" :options="opcionesMoneda" size="sm" disabled />
                        </td>
                        <td class="text-right" style="width: 110px;">
                          <input
                            :value="precioNetoFormateado[idx]"
                            @keypress="onPrecioNetoKeypress"
                            @input="e => onPrecioNetoInput(e.target.value, row, idx)"
                            @blur="e => onPrecioNetoBlur(e.target.value, row, idx)"
                            type="text" placeholder="0"
                            class="form-control form-control-sm text-right" />
                        </td>
                        <td class="text-right">{{ calcularMontoNeto(row.PrecioNeto, row.Cantidad) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </b-collapse>
              <b-collapse :visible="formData.tienePrecio === false" class="mt-2">
                <p class="mt-3 mb-0 text-muted" style="font-size: 0.9rem;">{{ $t('sin_precio_se_reciben_ofertas') }}</p>
              </b-collapse>
              <!-- Resto de la sección (Comisión, Visitas) -->
              <div class="mb-3 mt-3">
                <small class="form-label d-block mb-1 font-weight-bold mt-3">{{ $t('comision_inexlink') }}</small>

                <b-form-radio-group v-model="formData.tieneComision" :options="opcionesComision" name="comision"
                  size="sm" :class="{ 'is-invalid': !!errors.tieneComision }" @change="onChangeTieneComision" />
                <div v-if="errors.tieneComision" class="invalid-feedback d-block">{{ errors.tieneComision }}</div>

                <b-collapse :visible="formData.tieneComision === true" class="mt-3 mb-3">
                  <b-row>
                    <b-col md="4">
                      <small class="form-label d-block mb-1 font-weight-bold text-primary">{{ $t('porcentaje') }}</small>
                      <b-input-group size="sm">
                        <b-form-input v-model="formData.comisionPorcentaje" type="number" min="0.01" max="100"
                          step="0.01" placeholder="0.00" @input="validarPorcentaje"
                          :class="{ 'is-invalid': (formData.tieneComision === true) && (porcentajeInvalido || !!errors.comisionPorcentaje) }"
                          @blur="errors.comisionPorcentaje = (formData.tieneComision === true && (!formData.comisionPorcentaje || parseFloat(formData.comisionPorcentaje) <= 0)) ? $t('indique_porcentaje_comision') : null" />
                        <b-input-group-append>
                          <b-input-group-text>%</b-input-group-text>
                        </b-input-group-append>
                      </b-input-group>
                      <div v-if="(formData.tieneComision === true) && errors.comisionPorcentaje"
                        class="invalid-feedback d-block">{{ errors.comisionPorcentaje }}</div>
                    </b-col>
                  </b-row>

                </b-collapse>

                <div class="mt-4">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <small class="font-weight-bold">{{ $t('terminos_pago_label') }}</small>
                    <b-button size="sm" variant="outline-secondary" @click="usarTextoEjemplo">
                      {{ $t('usar_texto_ejemplo') }}
                    </b-button>
                  </div>
                  <b-form-textarea
                    v-model="formData.terminosPago"
                    rows="8"
                    style="font-size:12px;resize:vertical;"
                    :placeholder="$t('terminos_pago_label')"
                  />
                </div>
              </div>

              <div class="mb-3">
                <small class="form-label d-block mb-1 font-weight-bold">{{ $t('visitas_inventario') }}</small>
                <b-form-radio-group v-model="formData.aceptaVisita" :options="opcionesVisitas" name="visitas"
                  size="sm" :class="{ 'is-invalid': !!errors.aceptaVisita }" />
                <div v-if="errors.aceptaVisita" class="invalid-feedback d-block">{{ errors.aceptaVisita }}</div>
                <div v-if="formData.aceptaVisita === true" class="mt-2">
                  <small class="text-warning d-block">
                    <i class="fas fa-check-circle mr-1"></i>
                    {{ $t('visitas_aceptadas_texto') }}</small>
                </div>
                <div v-if="formData.aceptaVisita === false" class="mt-2">
                  <small class="text-warning d-block">
                    <i class="fas fa-ban mr-1"></i>
                    {{ $t('no_visitas') }}
                  </small>
                </div>
              </div>
            </b-card-body>
          </b-card>

          <!-- 5. Entrega y Logística -->
          <b-card class="compact-card mb-3">
            <b-card-body class="p-3">
              <h3 class="section-title mb-3">
                <span class="badge badge-primary mr-2">5</span>
                {{ $t('entrega_logistica') }}
              </h3>
              <b-row>
                <b-col md="4" class="mb-2">
                  <small class="form-label d-block mb-1 font-weight-bold">Incoterm *</small>
                  <b-form-select v-model="formData.idIncoterm" :options="opcionesIncoterm" size="sm"
                    :class="{ 'is-invalid': !!errors.idIncoterm }"
                    @change="errors.idIncoterm = !formData.idIncoterm ? $t('incoterm_requerido') : null" />
                  <div v-if="errors.idIncoterm" class="invalid-feedback d-block">{{ errors.idIncoterm }}</div>
                </b-col>
                <b-col md="4" class="mb-2">
                  <small class="form-label d-block mb-1 font-weight-bold">{{ $t('condicion_venta') }}</small>
                  <b-form-input :value="$t('as_is_where_is')" size="sm" readonly />
                </b-col>

                <b-col md="4" class="mb-2">
                  <small class="form-label d-block mb-1 font-weight-bold">{{ $t('condiciones_entrega') }}</small>
                  <b-form-select v-model="formData.idCondicionEntrega" :options="opcionesEntregaSelect" size="sm"
                    :class="{ 'is-invalid': !!errors.idCondicionEntrega }"
                    @change="errors.idCondicionEntrega = (!formData.idCondicionEntrega) ? $t('condicion_entrega_requerida') : null" />
                  <div v-if="errors.idCondicionEntrega" class="invalid-feedback d-block">{{ errors.idCondicionEntrega }}
                  </div>
                </b-col>
              </b-row>

              <div class="mt-3">
                <small class="form-label d-block mb-1 font-weight-bold">{{ $t('restricciones_observaciones') }}</small>
                <b-form-textarea v-model="formData.condicionEntregaObs" rows="2" size="sm"
                  :placeholder="$t('restricciones_placeholder')" />
              </div>

              <b-alert show variant="warning" class="mb-0 mt-2 p-2">
                <small>
                  <i class="fas fa-hard-hat mr-1"></i>
                  {{ $t('buyer_safety_texto') }}
                </small>
              </b-alert>
            </b-card-body>
          </b-card>

          <!-- 6. Plazos y Proceso de Venta -->
          <b-card class="compact-card mb-3">
            <b-card-body class="p-3">
              <h3 class="section-title mb-3">
                <span class="badge badge-primary mr-2">6</span>
                {{ $t('plazos_proceso_venta') }}
              </h3>
              <b-row>
                <b-col md="6" class="mb-2">
                  <small class="form-label d-block mb-1 font-weight-bold">{{ $t('plazo_entrega_dias') }} *</small>
                  <b-form-input v-model="formData.diasPlazoEntrega" type="number" size="sm" :placeholder="$t('plazo_entrega_placeholder')"
                    min="1" :class="{ 'is-invalid': !!errors.diasPlazoEntrega }" />
                  <small v-if="errors.diasPlazoEntrega" class="text-danger">{{ errors.diasPlazoEntrega }}</small>
                </b-col>

                <b-col md="6" class="mb-2">
                  <small class="form-label d-block mb-1 font-weight-bold">{{ $t('proceso_venta_label') }}</small>
                  <b-form-select v-model="formData.idProcesoVentaTipo" :options="opcionesProcesoSelect" size="sm"
                    :class="{ 'is-invalid': !!errors.idProcesoVentaTipo }"
                    @change="errors.idProcesoVentaTipo = (!formData.idProcesoVentaTipo) ? $t('proceso_venta_requerido') : null" />
                  <div v-if="errors.idProcesoVentaTipo" class="invalid-feedback d-block">{{ errors.idProcesoVentaTipo }}
                  </div>
                </b-col>

              </b-row>

              <b-collapse :visible="formData.idProcesoVentaTipo === '1'" class="mt-3">
                <b-row>
                  <b-col md="6">
                    <small class="form-label d-block mb-1 font-weight-bold">{{ $t('fecha_cierre_ofertas_label') }}</small>
                    <b-form-input v-model="formData.fechaCierreOfertas" type="datetime-local" size="sm"
                      :class="{ 'is-invalid': !!errors.fechaCierreOfertas }"
                      @blur="errors.fechaCierreOfertas = (formData.idProcesoVentaTipo === '1' && (!formData.fechaCierreOfertas)) ? $t('indique_fecha_cierre') : null" />
                    <div v-if="errors.fechaCierreOfertas" class="invalid-feedback d-block">{{ errors.fechaCierreOfertas
                    }}</div>
                  </b-col>
                </b-row>
              </b-collapse>

              <div class="mt-3 p-2 bg-light rounded">
                <small class="text-muted">
                  {{ $t('seleccion_ofertas_texto') }}
                </small> <br /><br />

                <small class="text-muted" v-if="formData.idProcesoVentaTipo === '1'">
                  {{ $t('ofertas_hasta_prefix') }} {{ formatFechaCierreOfertas() }}{{ $t('ofertas_hasta_suffix') }}
                </small><br /><br />

                <small class="text-muted">
                  {{ $t('fecha_adjudicacion_texto') }}
                </small>
              </div>
            </b-card-body>
          </b-card>

          <!-- 7. Información de Contacto -->
          <b-card class="compact-card mb-3">
            <b-card-body class="p-3">
              <h3 class="section-title mb-3">
                <span class="badge badge-primary mr-2">7</span>
                {{ $t('info_contacto_inexlink') }}
              </h3>
              <div class="form-row">
                <div class="col-md-6 mb-2">
                  <small class="form-label font-weight-bold">{{ $t('representante_ventas') }}</small>
                  <b-form-select v-model="formData.idUsuarioKam" :options="opcionesKam" size="sm"
                    :class="{ 'is-invalid': !!errors.idUsuarioKam }" />
                  <div v-if="errors.idUsuarioKam" class="invalid-feedback d-block">{{ errors.idUsuarioKam }}</div>
                </div>
              </div>
            </b-card-body>
          </b-card>

          <!-- 8. Anexos -->
          <b-card class="compact-card mb-3">
            <b-card-body class="p-3">
              <h3 class="section-title mb-3">
                <span class="badge badge-primary mr-2">8</span>
                {{ $t('ficha_venta_anexos') }}
              </h3>
              <div class="form-row">
                <div class="col-md-12 mb-2">
                  <b-form-select v-model="formData.idTipoAnexo" :options="opcionesAnexosSelect" size="sm"
                    :class="{ 'is-invalid': !!errors.idTipoAnexo }"
                    @change="errors.idTipoAnexo = (!formData.idTipoAnexo) ? 'Seleccione anexo' : null" />
                  <br /><br />
                  <input type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                    @change="onFileSelect" />

                  <!-- Textarea para observaciones -->
                  <div class="mt-3">
                    <small class="form-label d-block mb-1 font-weight-bold">{{ $t('observaciones_anexo') }}</small>
                    <b-form-textarea v-model="formData.txtAnexoObservacion" rows="3" size="sm"
                      :placeholder="$t('observaciones_anexo_placeholder')" maxlength="500" />
                    <small class="text-muted float-right">{{ (formData.txtAnexoObservacion || '').length }}/500</small>
                  </div>

                  <div v-if="archivosSeleccionados.length > 0" class="mt-2">
                    <small class="text-muted">
                      {{ archivosSeleccionados.length }} {{ $t('archivos_seleccionados_texto') }}
                    </small>
                    <ul class="list-unstyled">
                      <li v-for="(archivo, idx) in archivosSeleccionados" :key="idx" class="small text-muted">
                        <i class="fas fa-file mr-1"></i>{{ archivo.name }}
                      </li>
                    </ul>
                  </div>

                  <b-button v-if="puedeEscribir" size="sm" variant="info" class="mt-2" @click="subirArchivos"
                    :disabled="archivosSeleccionados.length === 0 || fotosLoading">
                    <b-spinner v-if="fotosLoading" small class="mr-1"></b-spinner>
                    <i v-else class="fas fa-cloud-upload-alt mr-1"></i>
                    {{ $t('subir_label') }} {{ archivosSeleccionados.length > 0 ? archivosSeleccionados.length + ' ' + $t('archivos_seleccionados_texto') : $t('archivo_label') }}
                  </b-button>

                  <!-- Sección de Anexos subidos -->
                  <div class="mt-4" v-if="fichaId">
                    <h5 class="mb-3">
                      <i class="fas fa-paperclip mr-2"></i>
                      {{ $t('anexos_subidos_titulo') }}
                    </h5>

                    <div v-if="anexosLoading" class="text-center py-3">
                      <b-spinner small class="mr-2"></b-spinner>
                      <small class="text-muted">{{ $t('cargando_anexos') }}</small>
                    </div>

                    <div v-else-if="anexosSubidos.length === 0" class="text-muted small py-3">
                      <i class="fas fa-info-circle mr-1"></i>
                      {{ $t('sin_anexos') }}
                    </div>

                    <div v-else class="table-responsive">
                      <table class="table table-sm table-bordered">
                        <thead class="thead-light">
                          <tr>
                            <th>{{ $t('tipo') }}</th>
                            <th>{{ $t('observacion') }}</th>
                            <th>{{ $t('fecha') }}</th>
                            <th>{{ $t('acciones') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="anexo in anexosSubidos" :key="anexo.id">

                            <td>
                              <small class="text-muted">{{ anexo.nombreTipoAdjunto }}</small>
                            </td>
                            <td>
                              <small>{{ anexo.observacion || '-' }}</small>
                            </td>
                            <td>
                              <small class="text-muted">
                                {{ anexo.fechaCreacion ? new Date(anexo.fechaCreacion).toLocaleDateString() : '-' }}
                              </small>
                            </td>
                            <td>
                              <a :href="anexo.url" target="_blank" class="btn btn-sm btn-outline-info mr-2"
                                style="text-decoration: none;">
                                <i class="fas fa-file"></i>
                              </a>

                              <b-button v-if="puedeEscribir" size="sm" variant="outline-danger" @click="eliminarAnexo(anexo.id)"
                                :disabled="anexosLoading">
                                <i class="fas fa-trash"></i>
                              </b-button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>


                </div>

              </div>
            </b-card-body>
          </b-card>


        </div>
        </fieldset>
      </b-container>
    </base-header>
  </div>
</template>

<script>
import fichaService from '@/api/fichaService.vue';
import utilsService from "@/api/utilsService";
import userService from '@/api/userService.vue';
import diccionarioService from '@/api/diccionarioService';
import categoriaImagenService from '@/api/categoriaImagenService.vue';
import { Select, Option } from 'element-ui';

export default {
  name: 'FichaVentaEquipos',
  components: { ElSelect: Select, ElOption: Option },
  props: {
    cluster:    { type: [Object], default: null },
    token:      { type: String, required: true },
    ficha:      { type: Object, default: null },
    idTipoFicha:{ type: Number, required: true },
    idCluster:  { type: [Number, String], default: null },
    idDetalle:  { type: [Number, String], default: null },
    idCriterio: { type: [Number, String], default: null },
    /**
     * Muestra la columna Multimedia en Información General y Técnica. Solo la activa
     * la lectura de ventas e inventario: el modal de subida vive en esa vista, así que
     * aquí únicamente se emite el evento con el material elegido.
     */
    mostrarMultimedia: { type: Boolean, default: false }
  },
  data() {
    return {
      isSubmitting: false,
      isSavingEquipos: false,
      isSavingPrecios: false,
      formData: {
        titulo: '',
        tituloIngles: '',
        resumenEjecutivo: '',
        resumenEjecutivoIngles: '',
        tienePrecio: null,
        cantidadTotal: null,
        detalleCantidad: '',
        tieneComision: null,
        comisionPorcentaje: null,
        aceptaVisita: null,
        idIncoterm: '',
        idCondicionEntrega: '',
        idCondicionDeVenta: '1',
        condicionEntregaObs: '',
        idProcesoVentaTipo: '',
        diasPlazoEntrega: null,
        fechaCierreOfertas: '',
        idUsuarioKam: null,
        idTipoFicha: 0,
        mailVendedor: '',
        idTipoAnexo: '',
        txtAnexoObservacion: '',
        terminosPago: ''
      },
      // Opciones para campos reactivos (computed para que reaccionen al idioma)
      opcionesMoneda: [
        { value: 'USD', text: 'USD' },
        { value: 'CLP', text: 'CLP' },
      ],
      // Estados de carga
      infoLoading: false,
      infoGeneral: [],
      infoGeneralOriginal: [],
      infoTerminosYCondiciones: [],
      infoTerminosYCondicionesOriginal: [],
      precioNetoFormateado: {},
      fotos: [],
      videos: [],
      fotosLoading: false,
      videosLoading: false,
      imagenCategoriaDefecto: null,
      errors: {},
      kamsOptions: [],
      archivosSeleccionados: [],
      anexosSubidos: [],
      anexosLoading: false,
      fichaId: null,
      expandirColumnas: false,
      tipoFichaSeleccionado: 1,
      condicionesOptions: [],
      condicionesEntregaOptions: [],
      // Reordenar items (estilo Trello)
      showModalMover: false,
      filaAMoverIdx: null,
      posicionMover: 1,
      guardandoOrden: false,
      ordenAscendente: true,
      columnaOrden: 'orden',
      infoOrdenOriginal: [],
      // Paginación de la tabla de materiales (performance con clusters grandes)
      currentPageMateriales: 1,
      pageSizeMateriales: 50,
    };
  },
  computed: {
    infoGeneralPaginado() {
      const start = (this.currentPageMateriales - 1) * this.pageSizeMateriales;
      return this.infoGeneral
        .map((row, idx) => ({ row, idx }))
        .slice(start, start + this.pageSizeMateriales);
    },
    totalPagesMateriales() {
      return Math.max(1, Math.ceil(this.infoGeneral.length / this.pageSizeMateriales));
    },
    opcionesComision() {
      return [
        { text: this.$t('si'), value: true },
        { text: this.$t('no'), value: false }
      ];
    },
    opcionesVisitas() {
      return [
        { text: this.$t('si'), value: true },
        { text: this.$t('no'), value: false }
      ];
    },
    opcionesPrecio() {
      return [
        { text: this.$t('si'), value: true },
        { text: this.$t('no'), value: false }
      ];
    },
    opcionesEntregaSelect() {
      const lang = localStorage.getItem('language') || 'es';
      const opts = this.condicionesEntregaOptions.map(o => ({
        value: String(o.id),
        text: lang === 'en' && o.nombreIngles ? o.nombreIngles : o.nombre
      }));
      return [{ value: '', text: this.$t('seleccione_condicion_entrega'), disabled: true }, ...opts];
    },
    opcionesProcesoSelect() {
      return [
        { value: '', text: this.$t('seleccione_proceso_venta'), disabled: true },
        { value: '1', text: this.$t('licitacion') },
        { value: '2', text: this.$t('venta_directa') }
      ];
    },
    opcionesIncoterm() {
      return [
        { value: '', text: this.$t('seleccione_incoterm'), disabled: true },
        { value: 'EXW', text: 'EXW - Ex Works' },
        { value: 'FCA', text: 'FCA - Free Carrier' },
        { value: 'CPT', text: 'CPT - Carriage Paid To' },
        { value: 'CIP', text: 'CIP - Carriage and Insurance Paid To' },
        { value: 'DAT', text: 'DAT - Delivered at Terminal' },
        { value: 'DAP', text: 'DAP - Delivered at Place' },
        { value: 'DDP', text: 'DDP - Delivered Duty Paid' },
        { value: 'FAS', text: 'FAS - Free Alongside Ship' },
        { value: 'FOB', text: 'FOB - Free on Board' },
        { value: 'CFR', text: 'CFR - Cost and Freight' },
        { value: 'CIF', text: 'CIF - Cost, Insurance and Freight' }
      ];
    },
    opcionesCondicionVentaSelect() {
      return [
        { value: '1', text: this.$t('as_is_where_is') }
      ];
    },
    opcionesAnexosSelect() {
      return [
        { value: '', text: this.$t('seleccione_tipo_anexo'), disabled: true },
        { value: '1', text: this.$t('anexo_1_texto') },
        { value: '2', text: this.$t('anexo_2_texto') },
        { value: '3', text: this.$t('anexo_3_texto') }
      ];
    },
    opcionesKam() {
      return [
        { value: null, text: this.$t('seleccione_representante') },
        ...this.kamsOptions.map(k => ({ value: k.value, text: k.text })),
      ];
    },
    resumenEjecutivoLength() {
      return this.formData.resumenEjecutivo ? this.formData.resumenEjecutivo.length : 0;
    },
    resumenEjecutivoInglesLength() {
      return this.formData.resumenEjecutivoIngles ? this.formData.resumenEjecutivoIngles.length : 0;
    },
    porcentajeInvalido() {
      if (this.formData.tieneComision !== true) return false;
      const v = parseFloat(this.formData.comisionPorcentaje);
      return isNaN(v) || v <= 0 || v > 100;
    },
    clusterId() {
      if (this.idCluster != null) return this.idCluster;
      const c = this.cluster;
      if (!c) return null;
      return c.clusterId || c.id || c.numeroCluster || null;
    },
    criterioId() {
      if (this.idCriterio != null) return this.idCriterio;
      const c = this.cluster;
      return c ? c.idCriterio || null : null;
    },
    canVerFicha() {
      return !!this.fichaId;
    },
    mostrarColumnaDescripcionCorta() {
      return this.expandirColumnas || this.infoGeneral.some(row => !!(row.partName || row.PartName));
    },
    mostrarColumnaNParte() {
      return this.expandirColumnas || this.infoGeneral.some(row => !!(row.numeroSerie || row.NumeroSerie));
    },
    mostrarColumnaFabricante() {
      return this.expandirColumnas || this.infoGeneral.some(row => !!(row.fabricante || row.Fabricante));
    },
    mostrarColumnaAnoFabricacion() {
      return this.expandirColumnas || this.infoGeneral.some(row => !!(row.anoFabricacion || row.AnoFabricacion));
    },
    mostrarColumnaHorasOperacion() {
      return this.expandirColumnas || this.infoGeneral.some(row => !!(row.horasOperacion || row.HorasOperacion));
    },
    mostrarColumnaUnidadMedida() {
      return this.expandirColumnas || this.infoGeneral.some(row => !!(row.unidadDeMedida || row.UnidadDeMedida));
    },
    mostrarColumnaModelo() {
      return this.expandirColumnas || this.infoGeneral.some(row => row.visibleModelo === true);
    },
    mostrarColumnaTamanoNeumaticos() {
      return this.expandirColumnas || this.infoGeneral.some(row => row.visibleTamanoCondicionNeumaticos === true);
    },
    mostrarColumnaHistorialMantenimiento() {
      return this.expandirColumnas || this.infoGeneral.some(row => row.visibleHistorialMantenimiento === true);
    },
    mostrarColumnaReparacionesRevisionesReciente() {
      return this.expandirColumnas || this.infoGeneral.some(row => row.visibleReparacionesRevisionesRecientes === true);
    },
    mostrarColumnaDimensiones() {
      return this.expandirColumnas || this.infoGeneral.some(row => row.visibleDimensiones === true);
    },
    mostrarColumnaTipo() {
      return this.expandirColumnas || this.infoGeneral.some(row => row.visibleTipo === true);
    },
    mostrarColumnaUsoRecomendado() {
      return this.expandirColumnas || this.infoGeneral.some(row => row.visibleUsoRecomendado === true);
    },
    mostrarColumnaSistemasCompatibles() {
      return this.expandirColumnas || this.infoGeneral.some(row => row.visibleSistemasCompatibles === true);
    },
    mostrarColumnaProfundidadBanda() {
      return this.expandirColumnas || this.infoGeneral.some(row => row.visibleProfundidadBanda === true);
    }
  },
  async mounted() {

    const tipoDeCluster = this.cluster
      ? (this.cluster['idTipoFicha'] || this.cluster['IdTipoFicha'])
      : null;
    this.tipoFichaSeleccionado = tipoDeCluster || this.idTipoFicha || 1;

    if (this.ficha) {
      this.fichaId = this.ficha.Id || this.ficha.id || null;
    }

    console.log('Valor cluster', this.cluster);

    this.cargarCondiciones();
    this.cargarCondicionesEntrega();
    this.cargarInfoGeneralYTecnica();
    this.cargarFichaTerminosYCondicionesPrecio();
    this.initFormFromFicha();
    this.cargarRegistroFotografico();
    this.cargarAnexosSubidos();

    await this.cargarKams();
  },
  watch: {
    ficha: {
      handler(newFicha) {
        this.fichaId = newFicha ? (newFicha.Id || newFicha.id || null) : null;
        this.initFormFromFicha();
        this.cargarAnexosSubidos();
      }
    },
    cluster: {
      deep: true,
      handler(newCluster) {
        this.tipoFichaSeleccionado = (newCluster && (newCluster.idTipoFicha || newCluster.IdTipoFicha)) || this.idTipoFicha || 1;
        this.cargarRegistroFotografico();
        this.cargarInfoGeneralYTecnica();
        this.cargarFichaTerminosYCondicionesPrecio();
      }
    },
    tipoFichaSeleccionado() {
      this.cargarInfoGeneralYTecnica();
      this.cargarFichaTerminosYCondicionesPrecio();
    },
    token() {
      this.cargarRegistroFotografico();
      this.cargarInfoGeneralYTecnica();
      this.cargarFichaTerminosYCondicionesPrecio();
    },
    idDetalle() {
      this.initFormFromFicha();
    }
  },
  methods: {
    usarTextoEjemplo() {
      this.formData.terminosPago =
`Español:
- Garantía: En caso de adjudicación, el cliente comprador deberá abonar el 15% del valor ofertado dentro de los 3 días hábiles siguientes a la notificación de adjudicación. Este pago actúa como garantía para reservar el inventario y avanzar con el proceso de venta.
 Respecto de la garantía, la única razón por la cual podrá ser devuelta es que el cliente vendedor desista de la transacción.
 En caso de no cumplir con este requisito en el plazo establecido, la oferta será anulada automáticamente.

-Pago del Saldo Final: El cliente comprador adjudicado deberá abonar el 85% restante del valor total de la transacción con al menos 2 días de antelación al retiro del inventario y, además, dentro del plazo que ocurra primero de los siguientes dos escenarios:
 1. Dentro de los 15 días corridos desde la aceptación de la orden de compra.
 2. Dentro del mes en curso en que se haya emitido la factura correspondiente.

 En caso de no cumplir con esta condición en los plazos establecidos, la oferta será anulada automáticamente y la garantía no será devuelta.

English:
- Guarantee: Upon award, the purchasing client must pay 15% of the offered value within 3 business days following the award notification. This payment acts as a guarantee to reserve the inventory and proceed with the sales process.
 Regarding the guarantee, the only reason it may be returned is if the selling client withdraws from the transaction.
 Failure to meet this requirement within the established deadline will result in the offer being automatically cancelled.

- Final Balance Payment: The awarded purchasing client must pay the remaining 85% of the total transaction value at least 2 days before inventory pickup, and also within whichever of the following two scenarios occurs first:
 1. Within 15 calendar days from the acceptance of the purchase order.
 2. Within the current month in which the corresponding invoice was issued.

 Failure to meet this condition within the established deadlines will result in the offer being automatically cancelled and the guarantee will not be returned.`;
    },

    async cargarCondiciones() {
      try {
        const data = await diccionarioService.getByTipo('CONDICION');
        this.condicionesOptions = data || [];
      } catch (e) {
        console.warn('[Condiciones] no se pudo cargar:', e);
      }
    },

    async cargarCondicionesEntrega() {
      try {
        const data = await diccionarioService.getByTipo('CON-ENTR');
        this.condicionesEntregaOptions = data || [];
      } catch (e) {
        console.warn('[CondicionesEntrega] no se pudo cargar:', e);
      }
    },
    resolveCondicion(id) {
      if (!id && id !== 0) return '—';
      const lang = localStorage.getItem('language') || 'es';
      const opt = this.condicionesOptions.find(o => String(o.id) === String(id));
      if (!opt) return id;
      return lang === 'en' ? (opt.nombreIngles || opt.nombre) : opt.nombre;
    },
    safe(obj, prop) {
      if (!obj) return '—';

      if (obj[prop] !== undefined && obj[prop] !== null && obj[prop] !== '') {
        return obj[prop];
      }

      const alternatives = {
        'Id': ['id', 'ID'],
        'Categoria': ['categoria'],
        'PartName': ['partName', 'partname'],
        'Fabricante': ['fabricante'],
        'Modelo': ['modelo'],
        'NumeroSerie': ['numeroSerie', 'numeroserie'],
        'AnoFabricacion': ['anoFabricacion', 'anofabricacion'],
        'HorasOperacion': ['horasOperacion', 'horasoperacion'],
        'Condicion': ['condicion'],
        'TamanoCondicionNeumaticos': ['tamanoCondicionNeumaticos', 'tamanocondicionneumaticos'],
        'HistorialMantenimiento': ['historialMantenimiento', 'historialmantenimiento'],
        'ReparacionesRevisionesRecientes': ['reparacionesRevisionesRecientes', 'reparacionesrevisionesrecientes'],
        'Ubicacion': ['ubicacion'],
        'Moneda': ['moneda'],
        'PrecioNeto': ['precioNeto', 'precioneto'],
        'Url': ['url', 'URL'],
        'FechaCreacion': ['fechaCreacion', 'fechacreacion'],
        'FechaSubida': ['fechaSubida', 'fechasubida'],
        'Nombre': ['nombre'],
        'ClusterId': ['clusterId', 'clusterid'],
        'Cantidad': ['cantidad'],
        'UnidadDeMedida': ['unidadDeMedida', 'unidaddemedida', 'UnidadMedida', 'unidadMedida']
      };

      if (alternatives[prop]) {
        for (const alt of alternatives[prop]) {
          if (obj[alt] !== undefined && obj[alt] !== null && obj[alt] !== '') {
            return obj[alt];
          }
        }
      }

      return '—';
    },

    initFormFromFicha() {
      const src = this.ficha;
      if (!src) {
        this.prellenarDesdeMaterialSiNoHayFicha();
        return;
      }

      const toStr = v => (v == null ? '' : String(v));
      const toNum = v => {
        if (v == null || v === '') return null;
        const n = parseFloat(v);
        return isNaN(n) ? null : n;
      };
      const toBool = v => {
        if (v === true || v === false) return v;
        if (v == null || v === '') return null;
        const s = String(v).toLowerCase();
        if (['true', '1', 'si', 'sí', 's'].includes(s)) return true;
        if (['false', '0', 'no', 'n'].includes(s)) return false;
        return null;
      };

      const hydrated = {
        titulo: toStr(src.titulo),
        tituloIngles: toStr(src.tituloIngles),
        resumenEjecutivo: toStr(src.resumenEjecutivo),
        resumenEjecutivoIngles: toStr(src.resumenEjecutivoIngles),
        tienePrecio: src.tienePrecio == null ? null : toBool(src.tienePrecio),
        cantidadTotal: null,
        detalleCantidad: '',
        tieneComision: toBool(src.tieneComision),
        comisionPorcentaje: toBool(src.tieneComision) === true ? toNum(src.comisionPorcentaje) : null,
        aceptaVisita: toBool(src.aceptaVisita),
        idIncoterm: toStr(src.idIncoterm),
        idCondicionEntrega: toStr(src.idCondicionEntrega),
        idCondicionDeVenta: toStr(src.idCondicionDeVenta),
        condicionEntregaObs: toStr(src.condicionEntregaObs || src.condicionEntregaTextoRestricion),
        idProcesoVentaTipo: toStr(src.idProcesoVentaTipo),
        diasPlazoEntrega: toNum(src.diasPlazoEntrega),
        fechaCierreOfertas: toStr(src.fechaCierreOfertas),
        mailVendedor: toStr(src.mailVendedor),
        idUsuarioKam: src.idUsuarioKam ? Number(src.idUsuarioKam) : (src.idKam ? Number(src.idKam) : null),
        terminosPago: toStr(src.terminosPago)
      };

      this.formData = Object.assign({}, this.formData, hydrated);
    },

    // Solo aplica a la ficha de un ítem individual (Cluster Detalle) que todavía no tiene
    // ficha creada: se pre-llenan Titulo/Resumen Ejecutivo (ES/EN) con el PartName/Detalles
    // de dbo.MaterialesFileETL (ya traducidos por translate_detalles.py), para que el usuario
    // no tenga que escribirlos desde cero.
    async prellenarDesdeMaterialSiNoHayFicha() {
      const idMaterialETL = this.idDetalle ? Number(this.idDetalle) : null;
      if (!idMaterialETL || idMaterialETL <= 0) return;

      try {
        const data = await fichaService.getPrellenadoDetalle(idMaterialETL);
        if (!data) return;

        this.formData.titulo = data.titulo || '';
        this.formData.tituloIngles = data.tituloIngles || '';
        this.formData.resumenEjecutivo = data.resumenEjecutivo || '';
        this.formData.resumenEjecutivoIngles = data.resumenEjecutivoIngles || '';
      } catch (e) {
        console.warn('[prellenarDesdeMaterialSiNoHayFicha] no se pudo cargar:', e);
      }
    },

    onChangeTieneComision() {
      this.errors.tieneComision = (this.formData.tieneComision === null) ? this.$t('comision_requerida') : null;
      if (this.formData.tieneComision === false) {
        this.formData.comisionPorcentaje = null;
        this.errors.comisionPorcentaje = null;
      }
    },

    validarPorcentaje() {
      if (this.formData.tieneComision !== true) return;
      let v = parseFloat(this.formData.comisionPorcentaje);
      if (isNaN(v)) return;
      if (v > 100) v = 100;
      if (v < 0.01 && v > 0) v = 0.01;
      this.formData.comisionPorcentaje = Math.round(v * 100) / 100;
    },

    async cargarInfoGeneralYTecnica() {
      const idCriterio = this.criterioId;
      const numeroCluster = this.cluster.nombreCluster || null;
      const idCluster = this.clusterId;
      const idDetalle = this.idDetalle || -1;
      const idFicha = this.fichaId || -1;

      console.log('cargarInfoGeneralYTecnica - idCriterio:', idCriterio, 'numeroCluster:', numeroCluster, 'token:', this.token, 'idCluster:', idCluster,'idDetalle:', idDetalle, 'idFicha:', idFicha);
      if (!this.token || !idCriterio) return;

      this.infoLoading = true;
      try {
        const lang = localStorage.getItem('language') || 'es';
        const data = await fichaService.getInfoGeneralYTecnica(this.token, idCriterio, numeroCluster, this.tipoFichaSeleccionado, idCluster, idDetalle, idFicha, lang);

        this.infoGeneral = Array.isArray(data) ? data : [];
        this.infoGeneralOriginal = JSON.parse(JSON.stringify(this.infoGeneral));
        this.infoOrdenOriginal = this.infoGeneral.map(f =>
          f.idMaterial || f.IdMaterial || f.Id || f.id
        );
        this.ordenAscendente = true;
        this.columnaOrden = 'orden';
        this.currentPageMateriales = 1;
        await this.cargarImagenCategoriaDefecto();
      } catch (e) {
        console.error('Error cargarInfoGeneralYTecnica:', e);
        this.infoGeneral = [];
        this.infoGeneralOriginal = [];
        this.imagenCategoriaDefecto = null;
        this.currentPageMateriales = 1;
      } finally {
        this.infoLoading = false;
      }
    },

    /**
     * Pide al contenedor que abra su modal de multimedia para este material.
     * El modal (subida de fotos y videos) vive en la vista padre, que ya lo usa
     * desde la búsqueda global: aquí solo se emite el material elegido.
     */
    abrirMultimedia(row) {
      const detalleId = row.idMaterial || row.IdMaterial || row.Id || row.id;
      if (!detalleId) return;

      // Se emite la fila misma, no una copia: el modal de la vista padre escribe
      // cantidadFotos y cantidadVideos sobre el objeto que recibe, así que el
      // botón queda al día apenas se sube o borra un archivo.
      this.$set(row, 'detalleId', detalleId);
      this.$set(row, 'tokenArchivo', this.token);
      if (row.cantidadFotos == null) this.$set(row, 'cantidadFotos', 0);
      if (row.cantidadVideos == null) this.$set(row, 'cantidadVideos', 0);

      this.$emit('abrir-multimedia', row);
    },

    /** El material ya tiene fotos o videos cargados. */
    tieneMultimedia(row) {
      return Number(row.cantidadFotos || 0) + Number(row.cantidadVideos || 0) > 0;
    },

    tituloMultimedia(row) {
      const fotos = Number(row.cantidadFotos || 0);
      const videos = Number(row.cantidadVideos || 0);
      if (!fotos && !videos) return this.$t('multimedia');
      return `${this.$t('fotos')}: ${fotos} · ${this.$t('videos')}: ${videos}`;
    },

    // La ficha siempre tiene al menos un registro en Información General y Técnica (sección 2).
    // Si no hay fotos subidas, se muestra la imagen por defecto de la CATEGORIA de ese primer registro.
    async cargarImagenCategoriaDefecto() {
      const categoria = this.infoGeneral.length ? this.safe(this.infoGeneral[0], 'categoria') : null;
      if (!categoria || categoria === '—') {
        this.imagenCategoriaDefecto = null;
        return;
      }
      try {
        const categorias = await categoriaImagenService.getAll();
        const target = String(categoria).trim().toLowerCase();
        const match = (categorias || []).find(c => {
          const nombreEs = String(c.categoria || c.Categoria || '').trim().toLowerCase();
          const nombreEn = String(c.categoriaIngles || c.CategoriaIngles || '').trim().toLowerCase();
          return nombreEs === target || nombreEn === target;
        });
        this.imagenCategoriaDefecto = match ? (match.urlImagenCategoria || match.UrlImagenCategoria || null) : null;
      } catch (e) {
        console.error('Error cargarImagenCategoriaDefecto:', e);
        this.imagenCategoriaDefecto = null;
      }
    },

    formatPrecio(val) {
      if (val == null || val === '' || Number(val) === 0) return '—';
      const lang = localStorage.getItem('language') || 'es';
      const locale = lang === 'en' ? 'en-US' : 'es-CL';
      return Number(val).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    },

    parsePrecioNetoLocale(val) {
      if (val === null || val === undefined || val === '') return null;
      const lang = localStorage.getItem('language') || 'es';
      let s = String(val).trim();
      s = lang === 'en' ? s.replace(/,/g, '') : s.replace(/\./g, '').replace(',', '.');
      const n = parseFloat(s);
      return Number.isNaN(n) ? null : n;
    },

    formatPrecioNeto(val) {
      if (val === null || val === undefined || val === '') return '';
      const numero = Number(val);
      if (Number.isNaN(numero)) return '';
      const lang = localStorage.getItem('language') || 'es';
      const locale = lang === 'en' ? 'en-US' : 'es-CL';
      return numero.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    },

    onPrecioNetoKeypress(e) {
      // El teclado numérico solo tiene ".", pero en español el decimal es ",".
      const lang = localStorage.getItem('language') || 'es';
      if (lang !== 'en' && e.key === '.') {
        e.preventDefault();
        const input = e.target;
        const start = input.selectionStart;
        const end = input.selectionEnd;
        const value = input.value;
        const newValue = value.slice(0, start) + ',' + value.slice(end);
        input.value = newValue;
        input.selectionStart = input.selectionEnd = start + 1;
        input.dispatchEvent(new Event('input'));
      }
    },

    onPrecioNetoInput(value, row, idx) {
      // Mientras el usuario escribe se deja el texto tal cual (sin reformatear en cada tecla).
      this.$set(this.precioNetoFormateado, idx, value);
      const parsed = this.parsePrecioNetoLocale(value);
      row.PrecioNeto = parsed != null ? parsed : '';
    },

    onPrecioNetoBlur(value, row, idx) {
      const parsed = this.parsePrecioNetoLocale(value);
      row.PrecioNeto = parsed != null ? parsed : '';
      this.$set(this.precioNetoFormateado, idx, parsed != null ? this.formatPrecioNeto(parsed) : '');
    },

    abrirModalMover(idx) {
      this.filaAMoverIdx = idx;
      this.posicionMover = (this.infoGeneral[idx] && this.infoGeneral[idx].orden) || (idx + 1);
      this.showModalMover = true;
    },

    async confirmarMoverItem() {
      const filas = this.infoGeneral;
      const total = filas.length;
      if (this.filaAMoverIdx == null || !total) return;

      let nuevaPos = Number(this.posicionMover);
      if (!nuevaPos || nuevaPos < 1) nuevaPos = 1;
      if (nuevaPos > total) nuevaPos = total;

      const idxActual = this.filaAMoverIdx;
      const [filaMovida] = filas.splice(idxActual, 1);
      filas.splice(nuevaPos - 1, 0, filaMovida);

      filas.forEach((f, i) => { f.orden = i + 1; });

      this.filaAMoverIdx = null;

      if (this.fichaId) {
        await this.persistirOrden();
      }
    },

    async persistirOrden() {
      const idCluster = this.clusterId;
      const items = (this.infoGeneral || [])
        .map(f => ({
          idMaterial: f.idMaterial || f.IdMaterial || f.Id || f.id,
          orden: f.orden
        }))
        .filter(i => i.idMaterial);

      if (!items.length || !this.fichaId || !idCluster) return;

      this.guardandoOrden = true;
      try {
        await fichaService.actualizarOrden(this.fichaId, idCluster, items);
        this.infoGeneralOriginal = JSON.parse(JSON.stringify(this.infoGeneral));
      } catch (e) {
        console.error('Error al guardar el nuevo orden:', e);
        if (this.$toast && this.$toast.error) {
          this.$toast.error(this.$t('error_guardar_orden'));
        }
      } finally {
        this.guardandoOrden = false;
      }
    },

    iconoOrden(columna) {
      if (this.columnaOrden !== columna) return '↕️';
      return this.ordenAscendente ? '↑' : '↓';
    },

    ordenarTabla(columna) {
      if (this.columnaOrden === columna) {
        this.ordenAscendente = !this.ordenAscendente;
      } else {
        this.columnaOrden = columna;
        this.ordenAscendente = true;
      }
      const stringCols = ['partName', 'categoria', 'numeroSerie', 'fabricante'];
      this.infoGeneral.sort((a, b) => {
        if (columna === 'precio') {
          const va = a.precioOriginal != null ? Number(a.precioOriginal) : -1;
          const vb = b.precioOriginal != null ? Number(b.precioOriginal) : -1;
          return this.ordenAscendente ? va - vb : vb - va;
        } else if (stringCols.includes(columna)) {
          const va = (a[columna] || '').toLowerCase();
          const vb = (b[columna] || '').toLowerCase();
          return this.ordenAscendente ? va.localeCompare(vb) : vb.localeCompare(va);
        } else {
          const va = a.orden != null ? a.orden : 99999;
          const vb = b.orden != null ? b.orden : 99999;
          return this.ordenAscendente ? va - vb : vb - va;
        }
      });
      this.infoGeneral.forEach((f, i) => { f.orden = i + 1; });
      if (this.fichaId) {
        this.persistirOrden();
      }
    },

    resetearOrden() {
      if (!this.infoOrdenOriginal.length) return;
      const posMap = {};
      this.infoOrdenOriginal.forEach((id, i) => { if (id != null) posMap[id] = i; });
      this.infoGeneral.sort((a, b) => {
        const ia = a.idMaterial || a.IdMaterial || a.Id || a.id;
        const ib = b.idMaterial || b.IdMaterial || b.Id || b.id;
        return (posMap[ia] != null ? posMap[ia] : 99999) - (posMap[ib] != null ? posMap[ib] : 99999);
      });
      this.infoGeneral.forEach((f, i) => { f.orden = i + 1; });
      this.ordenAscendente = true;
      this.columnaOrden = 'orden';
      if (this.fichaId) {
        this.persistirOrden();
      }
    },

    async cargarFichaTerminosYCondicionesPrecio() {
      const idCriterio = this.criterioId;
      const numeroCluster = this.cluster.nombreCluster || null;
      const idCluster = this.clusterId;
      const idDetalle = this.idDetalle || -1;
      const idFicha = this.fichaId || -1;

      if (!this.token || !idCriterio) return;

      this.infoLoading = true;
      try {
        const lang = localStorage.getItem('language') || 'es';
        const data = await fichaService.getFichaTerminosYCondicionesPrecio(this.token, idCriterio, numeroCluster, this.tipoFichaSeleccionado, idCluster, idDetalle, idFicha, lang);

        const normalizados = (Array.isArray(data) ? data : []).map(x => ({
          ...x,
          Id: x.Id || x.id || x.IdMaterial || x.idMaterial,
          Categoria: x.Categoria || x.categoria || '',
          PartName: x.PartName || x.partName || x.partname || '',
          Moneda: 'USD',
          PrecioNeto: x.PrecioNeto || x.precioNeto || x.precio || 0,
          Cantidad: x.cantidad || '',
          Detalle: x.detalle || '',
          Dimensiones: x.dimensiones || '',
          UnidadDeMedida: x.unidadDeMedida || '',
          Fabricante: x.fabricante || '',
          Modelo: x.modelo || '',
          Clase: x.clase || '',
          NumeroSerie: x.NumeroSerie || x.numeroSerie || x.numeroserie || ''
        }));

        console.log('cargarFichaTerminosYCondicionesPrecio normalizados:', normalizados);
        this.infoTerminosYCondiciones = normalizados;
        this.infoTerminosYCondicionesOriginal = JSON.parse(JSON.stringify(normalizados));

        this.precioNetoFormateado = {};
        normalizados.forEach((row, idx) => {
          this.precioNetoFormateado[idx] = this.formatPrecioNeto(row.PrecioNeto);
        });
      } catch (e) {
        console.error('Error cargarFichaTerminosYCondicionesPrecio:', e);
        this.infoTerminosYCondiciones = [];
        this.infoTerminosYCondicionesOriginal = [];
      } finally {
        this.infoLoading = false;
      }
    },


    async cargarKams() {
      try {
        const data = await userService.getKamOptions();
        this.kamsOptions = data || [];
      } catch {
        this.kamsOptions = [];
      }
    },

    async cargarRegistroFotografico() {
      const token = this.token;

      const idDetalle = this.idDetalle ? Number(this.idDetalle) : null;
      const idClusterVal = this.idCluster
        ? Number(this.idCluster)
        : (this.cluster ? (this.cluster.id || this.cluster.numeroCluster) : null);

      const idFamilia = idDetalle ? 2 : 1;
      const idItem    = idDetalle ? idDetalle : idClusterVal;

      if (!token || !idItem) return;

      this.fotosLoading = true;
      this.videosLoading = true;
      try {
        const todosArchivos = await fichaService.getArchivosByFamiliaAndItem(idFamilia, idItem);
        const fotosData  = todosArchivos.filter(x => Number(x.idTipoArchivo || x.IdTipoArchivo) === 2);
        const videosData = todosArchivos.filter(x => Number(x.idTipoArchivo || x.IdTipoArchivo) === 3);
        this.fotos = Array.isArray(fotosData) ? fotosData : [];
        this.videos = Array.isArray(videosData) ? videosData : [];
      } catch (e) {
        console.error('Error cargando registro fotográfico:', e);
        this.fotos = [];
        this.videos = [];
      } finally {
        this.fotosLoading = false;
        this.videosLoading = false;
      }
    },

    isYouTube(url) {
      if (!url) return false;
      return /youtu\.be|youtube\.com/.test(String(url).toLowerCase());
    },

    toYouTubeEmbed(url) {
      if (!url) return '';
      const u = String(url);
      const m1 = u.match(/youtu\.be\/([^?]+)/);
      const m2 = u.match(/v=([^&]+)/);
      const id = (m1 && m1[1]) || (m2 && m2[1]);
      return id ? `https://www.youtube.com/embed/${id}` : u;
    },

    // El PDF se abre en otra pestana y no dentro de un modal: los enlaces de un PDF no
    // admiten target="_blank", asi que al pinchar "Ver detalle completo" el visor navegaba
    // la pestana completa y se perdia la ficha, el cluster y el archivo seleccionados.
    // Con el PDF en su propia pestana, la aplicacion queda intacta pase lo que pase.
    verFichaPdf() {
      if (!this.canVerFicha) {
        this.showSwal('error', this.$t('no_ficha_preview'), this.$t('no_ficha_preview_msg'));
        return;
      }

      const url = fichaService.getFichaPdfUrl(this.fichaId, this.$getLanguage());
      const ventana = window.open(url, '_blank');

      if (ventana) {
        try { ventana.opener = null; } catch (e) { /* cross-origin: se ignora */ }
        return;
      }

      // Solo llega aca si el navegador bloquea las ventanas emergentes.
      this.showSwal('warning', this.$t('ficha_venta_pdf'), this.$t('popup_bloqueado_pdf'));
    },

    showSwal(icon, title, text) {
      if (this.$swal) {
        this.$swal({ icon, title, text });
        return;
      }
      if (this.$toast) {
        const msg = text || title || '';
        const map = {
          success: 'success',
          error: 'error',
          warning: 'warning',
          info: 'info',
          question: 'info'
        };
        const level = map[icon] || 'info';
        this.$toast[level](msg, { timeout: 3000 });
        return;
      }
      if (this.$bvToast) {
        this.$bvToast.toast(text || title, {
          variant: icon === 'success' ? 'success' : icon === 'warning' ? 'warning' : icon === 'info' ? 'info' : 'danger',
          autoHideDelay: 3000
        });
        return;
      }
      window.alert(`${title}${text ? `\n${text}` : ''}`);
    },

    validateForm() {
      const e = {};
      const f = this.formData;
      const requiredText = (val) => !val || String(val).trim().length === 0;

      e.resumenEjecutivo = requiredText(f.resumenEjecutivo) ? this.$t('ficha_venta_resumen_requerido') : null;
      e.tienePrecio = f.tienePrecio === null ? this.$t('precio_definido_requerido') : null;
      e.tieneComision = f.tieneComision === null ? this.$t('comision_requerida') : null;
      e.comisionPorcentaje = f.tieneComision === true
        ? (!f.comisionPorcentaje || parseFloat(f.comisionPorcentaje) <= 0 ? this.$t('indique_porcentaje_comision') : null)
        : null;
      e.aceptaVisita = f.aceptaVisita === null ? this.$t('visitas_requeridas') : null;
      const validIncoterms = this.opcionesIncoterm.filter(o => !o.disabled).map(o => o.value);
      e.idIncoterm = !validIncoterms.includes((f.idIncoterm || '').trim()) ? this.$t('incoterm_requerido') : null;
      e.idCondicionEntrega = requiredText(f.idCondicionEntrega) ? this.$t('condicion_entrega_requerida') : null;
      e.terminosPago = null;
      e.idCondicionDeVenta = null;
      const diasPlazo = Number(f.diasPlazoEntrega);
      e.diasPlazoEntrega = (!f.diasPlazoEntrega && f.diasPlazoEntrega !== 0) || isNaN(diasPlazo) || diasPlazo < 1
        ? this.$t('plazo_entrega_requerido')
        : null;
      e.idProcesoVentaTipo = requiredText(f.idProcesoVentaTipo) ? this.$t('proceso_venta_requerido') : null;
      e.idUsuarioKam = (!f.idUsuarioKam) ? this.$t('representante_requerido') : null;
      e.fechaCierreOfertas = f.idProcesoVentaTipo === '1' && requiredText(f.fechaCierreOfertas)
        ? this.$t('fecha_cierre_requerida')
        : null;

      this.errors = e;
      return Object.values(e).filter(Boolean);
    },

    async submitForm() {
      if (!this.puedeEscribir) return;
      const msgs = this.validateForm();
      if (msgs.length > 0) {
        this.showSwal('error', this.$t('faltan_campos'), this.$t('revisar_campos_rojo'));
        return;
      }

      this.isSubmitting = true;
      try {
        const token = this.token;
        const isEdit = !!(this.fichaId || (this.ficha && (this.ficha.id || this.ficha.Id)));
        const fichaIdExistente = this.fichaId || (this.ficha && (this.ficha.id || this.ficha.Id)) || null;
        const idUsuario = this.obtenerIdUsuarioActual() || 0;
        const idCluster = this.clusterId;

        const fechaCierreOfertasProcesada = (!this.formData.fechaCierreOfertas || this.formData.fechaCierreOfertas.trim() === '')
          ? '1900-01-01'
          : this.formData.fechaCierreOfertas;

        const intFields = {
          idCondicionEntrega: parseInt(this.formData.idCondicionEntrega) || 0,
          idCondicionDeVenta: parseInt(this.formData.idCondicionDeVenta) || 0,
          idProcesoVentaTipo: parseInt(this.formData.idProcesoVentaTipo) || 0,
        };

        const fichaDto = {
          ...this.formData,
          ...intFields,
          id: fichaIdExistente,
          token: token,
          idCluster: idCluster,
          idDetalle: this.idDetalle || -1,
          idTipoFicha: this.tipoFichaSeleccionado,
          comisionPorcentaje: this.formData.tieneComision ? parseFloat(this.formData.comisionPorcentaje) : null,
          fechaCierreOfertas: fechaCierreOfertasProcesada
        };

        let resp;
        if (isEdit && fichaIdExistente) {
          const request = { idUsuarioModificacion: idUsuario, ficha: fichaDto };
          resp = await fichaService.actualizar({ id: fichaIdExistente, request });
        } else {
          const payloadCreate = {
            ...this.formData,
            ...intFields,
            token: token,
            idUsuarioCreacion: idUsuario,
            id: fichaIdExistente,
            idCluster: idCluster,
            idDetalle: this.idDetalle || -1,
            idTipoFicha: this.tipoFichaSeleccionado,
            comisionPorcentaje: this.formData.tieneComision ? parseFloat(this.formData.comisionPorcentaje) : null,
            fechaCierreOfertas: fechaCierreOfertasProcesada
          };
          resp = await fichaService.create(payloadCreate);
        }

        if (!resp) {
          throw new Error('Error al guardar la ficha');
        }

        const finalFichaId = fichaIdExistente || resp.id || resp.Id || (resp.data && (resp.data.id || resp.data.Id)) || null;
        if (!finalFichaId) {
          throw new Error('No se obtuvo el id de la ficha del backend.');
        }

        this.fichaId = finalFichaId;
        this.isSavingEquipos = true;
        try {
          await this.guardarDatosEquipos(finalFichaId, idCluster, idUsuario, !isEdit);
          await this.guardarDatosPrecios(finalFichaId, idCluster, idUsuario);
          await this.$swal({
            icon: 'success',
            title: isEdit ? this.$t('ficha_actualizada_msg') : this.$t('ficha_guardada_msg'),
            text: this.$t('ficha_datos_guardados_msg'),
            confirmButtonText: this.$t('entendido'),
          });
          this.$emit('guardado', finalFichaId);

        } catch (e) {
          console.error('Error saving equipment data:', e);
          this.showSwal(
            'warning',
            this.$t('ficha_guardada_observaciones'),
            e.response.data.message || this.$t('no_todos_equipos_msg')
          );
        } finally {
          this.isSavingEquipos = false;
        }
      } catch (e) {
        console.error('Error saving record:', e);
        this.showSwal('error', this.$t('error_guardar_ficha'), e.message || this.$t('error'));
      } finally {
        this.isSubmitting = false;
      }
    },

    async guardarDatosEquipos(idFicha, idCluster, idUsuario, esNueva = false) {
      if (!idFicha) {
        throw new Error('Id de ficha requerido para guardar datos de equipos.');
      }
      if (!idCluster) {
        throw new Error('Cluster no disponible para guardar datos de equipos.');
      }

      const filas = Array.isArray(this.infoGeneral) ? this.infoGeneral : [];
      const items = [];

      for (let i = 0; i < filas.length; i += 1) {
        const fila = filas[i];
        if (!fila) {
          continue;
        }

        const idMaterial = fila.idMaterial || fila.IdMaterial || fila.Id || fila.id;
        if (!idMaterial) {
          console.warn('Fila sin idMaterial, se omite al guardar datos de equipos:', fila);
          continue;
        }

        let material = {
          idMaterial,
          TamanoCondicionNeumaticos: '',
          Ubicacion: '',
          ReparacionesRecientes: '',
          Tipo: '',
          UsoRecomendado: '',
          SistemasCompatibles: '',
          TipoMaterialTransportado: '',
          Dimensiones: '',
          ProfundidadBanda: '',
          Orden: fila.orden || fila.Orden || (i + 1)
        };

        const original = (this.infoGeneralOriginal && this.infoGeneralOriginal[i]) || null;

        if (this.tipoFichaSeleccionado === 1) {
          if (!esNueva && !this.filaEquiposCambiada(fila, original)) {
            continue;
          }
          material.TamanoCondicionNeumaticos = fila.tamanoCondicionNeumaticos,
          material.Ubicacion = fila.ubicacion,
          material.ReparacionesRecientes = fila.reparacionesRevisionesRecientes || '';
        }
        else if (this.tipoFichaSeleccionado === 2) {
          if (!esNueva && !this.filaEquiposCambiada(fila, original)) {
            continue;
          }
          material.TamanoCondicionNeumaticos = fila.tamanoCondicionNeumaticos;
          material.SistemasCompatibles = fila.sistemasCompatibles || '';
        }
        else if (this.tipoFichaSeleccionado === 3) {
          if (!esNueva && !this.filaEquiposCambiada(fila, original)) {
            continue;
          }
          material.Tipo = fila.tipo || '';
          material.UsoRecomendado = fila.usoRecomendado || '';
          material.SistemasCompatibles = fila.sistemasCompatibles || '';
          material.TipoMaterialTransportado = fila.tipoMaterialTransportado || '';
          material.Dimensiones = fila.dimensiones || '';
        }
        else if (this.tipoFichaSeleccionado === 4) {
          if (!esNueva && !this.filaEquiposCambiada(fila, original)) {
            continue;
          }
          material.Dimensiones = fila.dimensiones || '';
          material.Tipo = fila.tipo || '';
          material.TamanoCondicionNeumaticos = fila.tamanoCondicionNeumaticos;
          material.SistemasCompatibles = fila.sistemasCompatibles || '';
          material.ProfundidadBanda = fila.profundidadBanda || '';
        }

        items.push(material);
      }

      if (!items.length) {
        return;
      }

      await fichaService.guardarDatosEquiposBulk(idFicha, idCluster, idUsuario, items);
    },

    async guardarDatosPrecios(idFicha, idCluster, idUsuario) {
      if (!idFicha) {
        throw new Error('Id de ficha requerido para guardar precios.');
      }
      if (!idCluster) {
        throw new Error('Cluster no disponible para guardar precios.');
      }

      const filas = Array.isArray(this.infoTerminosYCondiciones) ? this.infoTerminosYCondiciones : [];
      const items = [];

      for (let i = 0; i < filas.length; i += 1) {
        const fila = filas[i];
        if (!fila) continue;

        const original = (this.infoTerminosYCondicionesOriginal && this.infoTerminosYCondicionesOriginal[i]) || null;
        if (!this.filaPrecioCambiada(fila, original)) {
          continue;
        }

        const idMaterial = fila.idMaterial || fila.IdMaterial || fila.Id || fila.id;
        if (!idMaterial) {
          console.warn('Fila sin idMaterial, se omite al guardar precios:', fila);
          continue;
        }

        items.push({
          idMaterial: idMaterial,
          moneda: fila.Moneda || fila.moneda || '',
          precio: fila.PrecioNeto || fila.precioNeto || fila.precio || 0
        });
      }

      if (items.length) {
        await fichaService.guardarPreciosBulk(idFicha, idCluster, idUsuario, items);
      }

      this.infoTerminosYCondicionesOriginal = JSON.parse(JSON.stringify(this.infoTerminosYCondiciones));
    },

    filaEquiposCambiada(actual, original) {
      if (!actual) return false;

      const norm = function (v) {
        return v == null ? '' : String(v);
      };

      if (!original) {
        return !!(
          norm(actual.TamanoCondicionNeumaticos) ||
          norm(actual.ReparacionesRecientes) ||
          norm(actual.Ubicacion) ||
          norm(actual.Tipo || actual.tipo) ||
          norm(actual.UsoRecomendado || actual.usoRecomendado) ||
          norm(actual.SistemasCompatibles || actual.sistemasCompatibles) ||
          norm(actual.TipoMaterialTransportado || actual.tipoMaterialTransportado) ||
          norm(actual.Dimensiones || actual.dimensiones)
        );
      }

      return (
        norm(actual.TamanoCondicionNeumaticos || actual.tamanoCondicionNeumaticos) !==
        norm(original.TamanoCondicionNeumaticos || original.tamanoCondicionNeumaticos) ||
        norm(actual.ReparacionesRecientes || actual.ReparacionesRevisionesRecientes || actual.reparacionesRevisionesRecientes) !==
        norm(original.ReparacionesRecientes || original.ReparacionesRevisionesRecientes || original.reparacionesRevisionesRecientes) ||
        norm(actual.Ubicacion || actual.ubicacion) !== norm(original.Ubicacion || original.ubicacion) ||
        norm(actual.Tipo || actual.tipo) !== norm(original.Tipo || original.tipo) ||
        norm(actual.UsoRecomendado || actual.usoRecomendado) !== norm(original.UsoRecomendado || original.usoRecomendado) ||
        norm(actual.SistemasCompatibles || actual.sistemasCompatibles) !== norm(original.SistemasCompatibles || original.sistemasCompatibles) ||
        norm(actual.TipoMaterialTransportado || actual.tipoMaterialTransportado) !== norm(original.TipoMaterialTransportado || original.tipoMaterialTransportado) ||
        norm(actual.Dimensiones || actual.dimensiones) !== norm(original.Dimensiones || original.dimensiones)
      );
    },

    filaPrecioCambiada(actual, original) {
      if (!actual) return false;

      const norm = function (v) {
        if (v == null) return '';
        if (typeof v === 'number') return String(v);
        return String(v);
      };

      if (!original) {
        return !!(norm(actual.Moneda || actual.moneda) || norm(actual.PrecioNeto || actual.precioNeto || actual.precio));
      }

      return (
        norm(actual.Moneda || actual.moneda) !== norm(original.Moneda || original.moneda) ||
        norm(actual.PrecioNeto || actual.precioNeto || actual.precio) !==
        norm(original.PrecioNeto || original.precioNeto || original.precio)
      );
    },

    resetForm() {
      if (!this.puedeEscribir) return;
      const defaults = {
        titulo: '',
        tituloIngles: '',
        resumenEjecutivo: '',
        resumenEjecutivoIngles: '',
        tienePrecio: null,
        cantidadTotal: null,
        detalleCantidad: '',
        tieneComision: null,
        comisionPorcentaje: null,
        aceptaVisita: null,
        idIncoterm: '',
        idCondicionDeVenta: '',
        idCondicionEntrega: '',
        condicionEntregaObs: '',
        idProcesoVentaTipo: '',
        diasPlazoEntrega: null,
        fechaCierreOfertas: '',
        idUsuarioKam: null,
        mailVendedor: ''
      };
      this.formData = Object.assign({}, this.formData, defaults);
      Object.keys(this.errors).forEach(k => { this.errors[k] = null; });
    },

    obtenerIdUsuarioActual() {
      try {
        const info = utilsService.getUserInfo ? utilsService.getUserInfo() : null;
        if (info && info.id) {
          const num = Number(info.id);
          return isNaN(num) ? null : num;
        }
      } catch (e) {
        console.warn("Could not retrieve current user:", e);
      }
      return null;
    },

    formatFechaCierreOfertas() {
      if (!this.formData.fechaCierreOfertas) {
        return this.$t('fecha_a_definir');
      }

      try {
        const fecha = new Date(this.formData.fechaCierreOfertas);
        const dia = String(fecha.getDate()).padStart(2, '0');
        const mes = String(fecha.getMonth() + 1).padStart(2, '0');
        const año = fecha.getFullYear();

        return `${dia}-${mes}-${año} at 23:59`;
      } catch (e) {
        return this.$t('fecha_a_definir');
      }
    },

    onFileSelect(e) {
      this.archivosSeleccionados = Array.from(e.target.files || []);
    },

    async subirArchivos() {
      if (!this.puedeEscribir) return;
      if (!this.archivosSeleccionados.length) {
        this.showSwal('warning', this.$t('sin_archivos_msg'), this.$t('seleccione_archivo_msg'));
        return;
      }

      const fichaId = this.fichaId;
      if (!fichaId) {
        this.showSwal('error', this.$t('error'), this.$t('guardar_ficha_primero'));
        return;
      }

      const form = new FormData();
      form.append('IdFicha', fichaId);
      form.append('IdSeccion', '8');
      form.append('Item', this.formData.idTipoAnexo || '1');
      form.append('Observacion', this.formData.txtAnexoObservacion || '');

      this.archivosSeleccionados.forEach(f => form.append('files', f));

      this.fotosLoading = true;
      try {
        await fichaService.subirArchivos(form);

        const clusterId = this.cluster ? (this.cluster.id || this.cluster.numeroCluster) : null;
        if (clusterId) {
          const fotosData = await fichaService.getFotosClusterResumen(this.token, clusterId);
          this.fotos = Array.isArray(fotosData) ? fotosData : [];
        }

        this.archivosSeleccionados = [];

        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.value = '';
        }

        this.formData.txtAnexoObservacion = '';

        this.showSwal('success', this.$t('exito'), this.$t('archivos_subidos_msg'));

        await this.cargarAnexosSubidos();
      } catch (e) {
        console.error('Error uploading files:', e);
        this.showSwal('error', this.$t('error_subir_archivos_msg'), e.message || this.$t('error'));
      } finally {
        this.fotosLoading = false;
      }
    },

    async cargarAnexosSubidos() {
      const fichaId = this.fichaId;
      if (!fichaId) {
        this.anexosSubidos = [];
        return;
      }

      this.anexosLoading = true;
      try {
        const data = await fichaService.getAnexosSubidos(fichaId);
        this.anexosSubidos = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error('Error al cargar anexos:', e);
        this.anexosSubidos = [];
      } finally {
        this.anexosLoading = false;
      }
    },

    async eliminarAnexo(idAnexo) {
      if (!this.puedeEscribir) return;
      if (!confirm(this.$t('confirmar_eliminar_anexo'))) {
        return;
      }

      this.anexosLoading = true;
      try {
        await fichaService.eliminarAnexo(idAnexo);
        this.showSwal('success', this.$t('exito'), this.$t('anexo_eliminado_msg'));
        await this.cargarAnexosSubidos();
      } catch (e) {
        console.error('Error deleting annex:', e);
        this.showSwal('error', this.$t('error'), this.$t('no_eliminar_anexo_msg'));
      } finally {
        this.anexosLoading = false;
      }
    },

    calcularMontoNeto(precioNeto, cantidad) {
      const precio = parseFloat(precioNeto) || 0;
      const cant = parseFloat(cantidad) || 0;
      const total = precio * cant;

      if (total === 0) return '—';

      const lang = localStorage.getItem('language') || 'es';
      const locale = lang === 'en' ? 'en-US' : 'es-CL';
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }).format(total);
    },
  },
};
</script>

<style scoped>
/* N° Parte: el valor puede llegar como una cadena larguísima sin espacios (números de
   200 dígitos), que sin un corte forzado estira la columna y descuadra toda la tabla.
   El ancho va fijo en 200px —con min-width, porque al cortar por carácter el contenido
   deja de empujar la columna y el navegador la colapsa— y el texto se parte en líneas. */
.col-nparte {
  width: 200px;
  min-width: 200px;
  max-width: 200px;
  white-space: normal;
  word-break: break-all;
  overflow-wrap: anywhere;
}
/* El <fieldset> que envuelve el formulario es solo un contenedor de permisos:
   no debe aportar borde, espaciado ni el min-width propio del elemento. */
fieldset {
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}
/* Modo solo lectura: bloquea también los controles que ignoran [disabled]
   (editores enriquecidos, drag & drop) sin ocultar el contenido. */
.ficha-solo-lectura [contenteditable="true"] {
  pointer-events: none;
}

.th-ordenable {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}
.th-ordenable:hover {
  background-color: #e9ecef !important;
}
.th-ordenable:active {
  background-color: #dee2e6 !important;
}

.compact-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.compact-card .card-body {
  text-align: left;
}

.section-title {
  color: #495057;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.form-label {
  color: #6c757d;
  font-size: 12px;
}

.termino-compacto {
  padding: 8px;
  background: white;
  border-radius: 4px;
  border-left: 3px solid #ffc107;
}

.termino-compacto strong {
  font-size: 13px;
}

.termino-compacto p {
  font-size: 11px;
  line-height: 1.3;
  margin-top: 2px;
}

.equipo-form,
.precio-form {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
}

.form-control-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.is-invalid {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.table-responsive {
  font-size: 0.85rem;
}

.table th,
.table td {
  padding: 0.5rem;
  vertical-align: middle;
}

.saving-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.saving-dialog {
  text-align: center;
  padding: 2rem;
  border-radius: 0.5rem;
  background: #343a40;
  color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}
</style>
